import type { Alarm } from './types';
import { PROTOCOL_LABELS, buildLifecycleBadge, formatRoDate, formatRelativeAge, type LifecycleInfo } from './config';
import { state, api, $, $$, toast, escHtml, truncate, todayStr, _timeAgo, _updateHelpBadge, _ensureMachineCache, _allMachinesCache, _lineMap, _lineLabels, _populateLineSelect, _linkLineToMachineSelect, updateAlarmBadge, computeAlerts, recordHoursHistory, buildSparklineSVG, formatDateRO, formatDateTimeRO, intervalHoursProgress, shouldShowMaintenanceInterval, openAlarmHistoryForMachine, badgeStare, oreRamaseStare } from './core';
import { openControllerAlarmDetailModal } from './alarm-manual';
import { getHourlyAvgPower, getLineAttributedPower, getSensorValue, getSensorRaw, isSensorRealtime, getSensorSeverity, buildGaugeSVG, getSensorFreshness, getSensorUpdatedAt } from './overview';
import { fetchMachinePhotos, openLightbox as openMachineLightbox, type LightboxPhoto } from './lightbox';
import { askText, askConfirm } from './dialogs';
import { renderEticheteTab } from './etichete';
import { renderImprimantaTab } from './imprimanta';

const PROTO_LBL = PROTOCOL_LABELS as Record<string, string>;

// 11 sep 2026 — «Fel»-ul lucrării (maintenance_intervals.natura, routes/documents/natura_lucrarii.py)
const NATURA_ETICHETE: Record<string, string> = {
    periodica: 'Periodică',
    la_conditie: 'La condiție',
    punere_in_functiune: 'Punere în funcțiune',
    la_eveniment: 'La eveniment',
    reglaj: 'Reglaj',
    strangere: 'Cuplu de strângere',
    procedura: 'Procedură',
    nedeterminata: 'Nedeterminată',
};
const NATURA_ORDINE = Object.keys(NATURA_ETICHETE);

/** Wired from auth-ui in the bundle (avoid circular import). */
declare function _renderDocumentTemplateNavigator(d: any): string;
declare function _buildTreeSectionOptions(sections: any[], includeEmpty: boolean): string;
declare function refreshDocumentProcessingStatus(): void;
declare function openProcedureOverlay(...args: any[]): any;
declare function _renderEnergyMonitor(...args: any[]): any;

/* ═══ LINE DETAIL PANEL ═══════════════════════════════════════ */
export let detailTabActive: string = 'parts';

function _canToggleMaintenanceIntervals(): boolean {
    return !!(state.currentUser && (state.currentUser.role === 'engineer' || state.currentUser.role === 'master'));
}

/** Re-fetch line detail after interval toggle so enabled/disabled lists stay in sync. */
async function _refreshDetailDataAndRender(): Promise<void> {
    const lineId = (window as any)._detailLineId;
    if (lineId) {
        try {
            const fresh = await api(`/api/line_detail/${encodeURIComponent(lineId)}`);
            (window as any)._detailData = fresh;
        } catch {
            /* keep stale cache; renderDetailTab still runs */
        }
    }
    await renderDetailTab();
}

/* Auto-reîmprospătare detaliu linie (31 aug 2026, cerut de Dan după panoul de
 * utilaj). Ecranul rămânea înghețat cât îl țineai deschis. Aici se lucrează —
 * taburi, filtre, dezactivări de intervale — deci turul se sare când ar putea
 * strica ceva:
 *   - dacă e deschisă revizia/workflow-ul peste el (om în mijlocul lucrării);
 *   - dacă utilizatorul are focus pe ceva din ecran (tastează / navighează);
 *   - dacă fereastra nu e vizibilă (alt tab în browser).
 * Tabul activ și filtrele trăiesc în variabile globale, deci se păstrează
 * singure; salvăm în plus poziția de derulare. */
const LD_REFRESH_MS = 20000;
let _ldTimer: number | null = null;

function _ldStopRefresh(): void {
    if (_ldTimer !== null) { clearInterval(_ldTimer); _ldTimer = null; }
}

function _ldStartRefresh(): void {
    _ldStopRefresh();
    _ldTimer = window.setInterval(async () => {
        const ov = document.getElementById('detail-overlay');
        if (!ov || ov.classList.contains('hidden')) { _ldStopRefresh(); return; }
        const rev = document.getElementById('revision-detail-overlay');
        if (rev && !rev.classList.contains('hidden')) return;   // lucrare în curs
        if (document.hidden) return;                            // tab în fundal
        const el = document.getElementById('detail-content');
        const activ = document.activeElement;
        if (el && activ && el.contains(activ) && activ !== document.body) return;
        const scrollAnt = el ? el.scrollTop : 0;
        try {
            await _refreshDetailDataAndRender();
        } catch { /* eroare trecătoare — păstrăm ce e pe ecran */ }
        const el2 = document.getElementById('detail-content');
        if (el2) el2.scrollTop = scrollAnt;
    }, LD_REFRESH_MS);
}

/** Collapsible audit list for enabled=0 intervals (URMĂTOAREA REVIZIE + INTERVALE). */
/** 11 sep 2026: «Cuplurile de strângere» ale mașinii — catalog determinist scos din textul
 *  manualelor (`scripts/extrage_cupluri_strangere.py`, tabel `cupluri_strangere`). Erau
 *  fraze pierdute prin proceduri («MA Şurub de prindere M8: 25 Nm», p.1195) sau lucrări
 *  de 0 h; acum stau sub tab-ul Intervale, pe filet, cu pagina din manual. Se încarcă
 *  leneș, după ce tabelul de intervale e deja pe ecran. */
function renderCupluriSection(el: HTMLElement, machineId: string): void {
    const box = document.createElement('div');
    box.id = 'iv-cupluri';
    box.style.marginTop = '18px';
    el.appendChild(box);
    api('/api/machines/' + encodeURIComponent(machineId) + '/cupluri').then((r: any) => {
        const cupluri: any[] = (r && r.cupluri) || [];
        if (!cupluri.length) return;
        const filete = [...new Set(cupluri.map((c: any) => c.filet || '').filter(Boolean))] as string[];
        let html = `<h4 style="margin:0 0 6px;display:flex;align-items:center;gap:8px">🔩 Cupluri de strângere (N·m)`
            + ` <span class="badge badge-blue">${cupluri.length}</span>`
            + `<span style="font-size:.78em;font-weight:normal;opacity:.7">din manual, pe filet — verifică pagina înainte de a strânge</span></h4>`;
        if (filete.length > 1) {
            html += '<div style="margin-bottom:6px"><label style="font-size:.85em">Filet: <select id="cupluri-filet"><option value="">Toate</option>';
            filete.forEach(f => { html += `<option value="${escHtml(f)}">${escHtml(f)}</option>`; });
            html += '</select></label></div>';
        }
        html += '<table><thead><tr><th>Filet</th><th>Cuplu</th><th>Componentă</th><th>Clasă</th><th>Manual</th></tr></thead><tbody>';
        cupluri.forEach((c: any) => {
            const cuplu = c.cuplu_max_nm && c.cuplu_max_nm !== c.cuplu_nm
                ? `${c.cuplu_nm}–${c.cuplu_max_nm} Nm` : `${c.cuplu_nm} Nm`;
            const orig = c.valoare_text && !/N\s?[·.]?\s?m/i.test(c.valoare_text)
                ? ` <span style="opacity:.6;font-size:.85em">(${escHtml(c.valoare_text)})</span>` : '';
            const pdf = c.document_id && c.page_number
                ? `<a href="/api/documents/${c.document_id}/view?page=${c.page_number}" target="_blank" class="btn btn-sm" style="background:rgba(0,180,255,.08);border-color:rgba(0,180,255,.25);color:#00b4ff;text-decoration:none;white-space:nowrap">PDF p.${c.page_number}</a>`
                : (c.document_id ? `<a href="/api/documents/${c.document_id}/view" target="_blank" class="btn btn-sm" style="color:#00b4ff;text-decoration:none">PDF</a>` : '');
            const sursa = c.sursa === 'interval' ? ' <span class="badge" style="font-size:.7em" title="scos din lucrarea #' + c.interval_id + '">lucrare</span>' : '';
            html += `<tr data-filet="${escHtml(c.filet || '')}"><td><strong>${escHtml(c.filet || '—')}</strong></td>`
                + `<td><strong>${cuplu}</strong>${orig}</td>`
                + `<td title="${escHtml(c.context || '')}">${escHtml(c.componenta || '')}${sursa}</td>`
                + `<td>${escHtml(c.clasa || '')}</td><td>${pdf}</td></tr>`;
        });
        html += '</tbody></table>';
        box.innerHTML = html;
        document.getElementById('cupluri-filet')?.addEventListener('change', (e) => {
            const v = (e.target as HTMLSelectElement).value;
            box.querySelectorAll('tbody tr').forEach((tr: Element) => {
                (tr as HTMLElement).style.display = (!v || (tr as HTMLElement).dataset.filet === v) ? '' : 'none';
            });
        });
    }).catch((ex: any) => { console.warn('cupluri de strângere:', ex); });
}

function renderDisabledIntervalsSection(
    disabledList: any[],
    opts: { summarySuffix?: string } = {},
): string {
    if (!disabledList.length) return '';
    const canToggle = _canToggleMaintenanceIntervals();
    const suffix = opts.summarySuffix || '';
    const openAttr = canToggle ? ' open' : '';
    let html = `<details class="disabled-intervals-section"${openAttr} style="margin-top:1em">`;
    html += `<summary class="disabled-intervals-summary">⚠ ${disabledList.length} intervale dezactivate${suffix ? ' ' + suffix : ''} <span class="disabled-intervals-hint">(click pentru detalii)</span></summary>`;
    html += '<table class="detail-revision-table disabled-intervals-table"><thead><tr>';
    html += '<th>Interval</th><th>Acțiune</th><th>Motiv</th><th>Dezactivat de</th>';
    if (canToggle) html += '<th>Reactivare</th>';
    html += '</tr></thead><tbody>';
    disabledList.forEach((iv: any) => {
        const reason = (iv.disabled_reason || '').trim() || '(nespecificat)';
        const by = (iv.disabled_by || '').trim() || '(nespecificat)';
        html += `<tr><td>${escHtml(iv.interval_text || '—')}</td><td>${escHtml(iv.action_ro || '')}</td>`;
        html += `<td class="disabled-intervals-reason">${escHtml(reason)}</td>`;
        html += `<td class="disabled-intervals-by">${escHtml(by)}</td>`;
        if (canToggle) {
            html += `<td><button type="button" class="btn btn-xs iv-toggle-enable" data-iv-id="${iv.id}" style="background:rgba(76,175,80,.15);border:1px solid rgba(76,175,80,.4);color:#4caf50;cursor:pointer;padding:2px 8px">Activează</button></td>`;
        }
        html += '</tr>';
    });
    html += '</tbody></table></details>';
    return html;
}

/* Commissioning row în antetul modalului (date punere în funcțiune).
 * Render sincron pe baza datelor deja prezente pe `m` (returnate de
 * /api/machines/all + /api/line_detail/<id>); badge-ul de vârstă se
 * îmbogățește asincron prin _enrichCommissioningRow(). */
function _renderCommissioningRow(m: any): string {
    const cd = (m && m.commissioning_date) || null;
    const md = (m && m.manufacture_date) || null;
    const note = (m && m.commissioning_notes) || '';
    if (!cd && !md) {
        return `<div class="detail-info-item detail-info-item-commissioning">
            <span class="detail-info-label">Pus în funcțiune</span>
            <span class="detail-info-value" style="color:var(--text-muted);font-style:italic">necunoscut <span class="lifecycle-badge-slot" data-machine-id="${escHtml(m && m.id || '')}"></span></span>
        </div>`;
    }
    const ref = cd || md;
    let dateStr = formatRoDate(ref);
    if (cd && md && cd !== md) {
        dateStr += ` (fab. ${formatRoDate(md)})`;
    } else if (!cd && md) {
        dateStr = `${dateStr} (fabricație)`;
    }
    const noteStr = note ? ` — ${escHtml(truncate(note, 80))}` : '';
    return `<div class="detail-info-item detail-info-item-commissioning">
        <span class="detail-info-label">Pus în funcțiune</span>
        <span class="detail-info-value">${escHtml(dateStr)} <span class="lifecycle-badge-slot" data-machine-id="${escHtml(m && m.id || '')}"></span>${noteStr}</span>
    </div>`;
}

async function _enrichCommissioningRow(machineId: string): Promise<void> {
    if (!machineId) return;
    const slot = document.querySelector(`#detail-machine-info .lifecycle-badge-slot[data-machine-id="${CSS.escape(machineId)}"]`) as HTMLElement | null;
    if (!slot) return;
    try {
        const info = await api(`/api/config/machines/${encodeURIComponent(machineId)}/lifecycle`) as LifecycleInfo;
        const b = buildLifecycleBadge(info);
        const rel = formatRelativeAge(info.age_days);
        slot.innerHTML = b.html + (rel ? ` <span style="color:var(--text-muted);font-size:.65rem">(${rel})</span>` : '');
    } catch {
        slot.innerHTML = '';
    }
}

export function setDetailTabActive(tab: string | undefined): void {
    if (typeof tab === 'string') detailTabActive = tab;
}

async function _showOperatingHoursGraph(machineId: string, machineName: string): Promise<void> {
    const existing = document.getElementById('ophours-graph-overlay');
    if (existing) existing.remove();
    const overlay = document.createElement('div');
    overlay.id = 'ophours-graph-overlay';
    overlay.className = 'proc-overlay';
    overlay.innerHTML = `<div class="proc-container" style="max-width:760px;overflow-y:auto">
        <div class="proc-header"><div class="proc-header-top">
            <h2 class="proc-title">📈 Istoric ore — ${machineName}</h2>
            <button class="proc-close" id="ophg-close">&times;</button>
        </div></div>
        <div class="proc-body" style="padding:14px">
            <div style="display:flex;gap:8px;align-items:center;margin-bottom:10px;font-size:.85rem">
                <label>Interval:</label>
                <select id="ophg-days" style="padding:4px;border-radius:6px;border:1px solid var(--border);background:var(--bg-main);color:var(--text-main)">
                    <option value="7">7 zile</option>
                    <option value="30" selected>30 zile</option>
                    <option value="90">90 zile</option>
                    <option value="365">1 an</option>
                </select>
                <button class="btn btn-sm" id="ophg-snapshot" style="margin-left:auto">📸 Snapshot acum</button>
            </div>
            <div id="ophg-summary" style="margin-bottom:10px;padding:10px;background:var(--surface);border-radius:8px;font-size:.85rem"></div>
            <div id="ophg-canvas-wrap" style="position:relative;height:280px;background:var(--bg-main);border:1px solid var(--border);border-radius:8px;padding:8px">
                <canvas id="ophg-canvas" style="width:100%;height:100%"></canvas>
            </div>
            <div id="ophg-resets" style="margin-top:14px"></div>
        </div>
    </div>`;
    document.body.appendChild(overlay);
    document.getElementById('ophg-close')?.addEventListener('click', () => overlay.remove());

    const loadGraph = async () => {
        const days = parseInt((document.getElementById('ophg-days') as HTMLSelectElement)?.value || '30');
        try {
            const r = await fetch(`/api/machines/${encodeURIComponent(machineId)}/operating_hours/history?days=${days}`);
            const d = await r.json();
            const summary = document.getElementById('ophg-summary');
            if (summary) {
                summary.innerHTML = `<strong>Ore curente:</strong> ${(d.current_hours || 0).toFixed(1)} h
                    ${(d.current_offset || 0) > 0 ? `<span style="color:#f59e0b;margin-left:10px">⚠ Offset cumulativ: +${d.current_offset.toFixed(1)} h (după reset ECU)</span>` : ''}
                    <span style="margin-left:14px;color:var(--text-muted)">Puncte istoric: ${(d.points || []).length}</span>`;
            }
            _drawOperatingHoursChart('ophg-canvas', d.points || []);
        } catch (e) { console.error(e); }
        try {
            const rr = await fetch('/api/operating_hours/resets?unacknowledged=1');
            const resets = await rr.json();
            const filtered = (resets || []).filter((r: any) => r.machine_id === machineId);
            const wrap = document.getElementById('ophg-resets');
            if (wrap) {
                if (filtered.length === 0) {
                    wrap.innerHTML = '<div style="color:var(--text-muted);font-size:.85rem">Nu sunt reset-uri ECU neconfirmate.</div>';
                } else {
                    wrap.innerHTML = '<h3 style="font-size:.95rem;margin:0 0 6px 0">⚠ Reset-uri ECU detectate:</h3>' +
                        filtered.map((r: any) => `<div style="padding:8px;background:#fef3c7;color:#000;border-radius:6px;margin-bottom:4px;font-size:.85rem">
                            <strong>${r.detected_at}</strong> — raportat ${r.reported_value.toFixed(1)}h, anterior ${r.previous_value.toFixed(1)}h, offset adăugat <strong>+${r.offset_added.toFixed(1)}h</strong>
                            <button class="btn btn-sm" data-rid="${r.id}" style="margin-left:8px;font-size:.7rem;padding:2px 8px">Confirm</button>
                        </div>`).join('');
                    wrap.querySelectorAll('button[data-rid]').forEach(btn => {
                        btn.addEventListener('click', async () => {
                            const rid = (btn as HTMLButtonElement).dataset.rid;
                            await fetch(`/api/operating_hours/resets/${rid}/acknowledge`, { method: 'POST' });
                            loadGraph();
                        });
                    });
                }
            }
        } catch (e) { /* ignore */ }
    };

    document.getElementById('ophg-days')?.addEventListener('change', loadGraph);
    document.getElementById('ophg-snapshot')?.addEventListener('click', async () => {
        try {
            const r = await fetch(`/api/machines/${encodeURIComponent(machineId)}/operating_hours/snapshot`, { method: 'POST' });
            if (!r.ok) {
                const j = await r.json().catch(() => ({}));
                alert('Eroare snapshot: ' + (j.error || r.status));
                return;
            }
            loadGraph();
        } catch (e) { alert('Eroare: ' + e); }
    });
    loadGraph();
}

function _drawOperatingHoursChart(canvasId: string, points: any[]): void {
    const canvas = document.getElementById(canvasId) as HTMLCanvasElement;
    if (!canvas) return;
    const wrap = canvas.parentElement!;
    canvas.width = wrap.clientWidth - 16;
    canvas.height = wrap.clientHeight - 16;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (!points || points.length === 0) {
        ctx.fillStyle = '#888';
        ctx.font = '14px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('Niciun punct în interval. Folosește "Snapshot acum" pentru a începe înregistrarea.', canvas.width / 2, canvas.height / 2);
        return;
    }
    const padL = 50, padR = 12, padT = 12, padB = 28;
    const w = canvas.width - padL - padR;
    const h = canvas.height - padT - padB;
    const vals = points.map((p: any) => p.cumulative != null ? p.cumulative : p.hours);
    const minV = Math.min(...vals);
    const maxV = Math.max(...vals);
    const rangeV = (maxV - minV) || 1;
    const times = points.map((p: any) => new Date(p.recorded_at).getTime());
    const minT = Math.min(...times);
    const maxT = Math.max(...times);
    const rangeT = (maxT - minT) || 1;
    ctx.strokeStyle = '#444';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(padL, padT);
    ctx.lineTo(padL, padT + h);
    ctx.lineTo(padL + w, padT + h);
    ctx.stroke();
    ctx.fillStyle = '#888';
    ctx.font = '11px sans-serif';
    ctx.textAlign = 'right';
    for (let i = 0; i <= 4; i++) {
        const y = padT + h - (h * i / 4);
        const v = minV + rangeV * i / 4;
        ctx.fillText(v.toFixed(0) + 'h', padL - 4, y + 3);
        ctx.strokeStyle = '#222';
        ctx.beginPath();
        ctx.moveTo(padL, y);
        ctx.lineTo(padL + w, y);
        ctx.stroke();
    }
    ctx.strokeStyle = '#22c55e';
    ctx.lineWidth = 2;
    ctx.beginPath();
    points.forEach((p: any, i: number) => {
        const x = padL + ((times[i] - minT) / rangeT) * w;
        const v = p.cumulative != null ? p.cumulative : p.hours;
        const y = padT + h - ((v - minV) / rangeV) * h;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
    });
    ctx.stroke();
    ctx.fillStyle = '#22c55e';
    points.forEach((p: any, i: number) => {
        const x = padL + ((times[i] - minT) / rangeT) * w;
        const v = p.cumulative != null ? p.cumulative : p.hours;
        const y = padT + h - ((v - minV) / rangeV) * h;
        ctx.beginPath();
        ctx.arc(x, y, 2.5, 0, Math.PI * 2);
        ctx.fill();
    });
    ctx.fillStyle = '#888';
    ctx.textAlign = 'center';
    const fmtDate = (t: number) => {
        const d = new Date(t);
        return `${d.getDate()}/${d.getMonth() + 1}`;
    };
    ctx.fillText(fmtDate(minT), padL, padT + h + 16);
    ctx.fillText(fmtDate(maxT), padL + w, padT + h + 16);
}

export async function openLineDetail(lineId: string, product?: string, source?: string): Promise<void> {
    (window as any)._detailSource = source || 'master';
    $('#detail-line-id')!.textContent = lineId;
    $('#detail-line-product')!.textContent = product || '';

    ($('#detail-machine-info') as HTMLElement).innerHTML = '<div class="detail-loading"><div class="spinner"></div>Se încarcă...</div>';
    ($('#detail-content') as HTMLElement).innerHTML = '';

    $$('.detail-tab').forEach((t: Element) => (t as HTMLElement).style.display = 'none');

    $('#detail-overlay')!.classList.remove('hidden');
    _ldStartRefresh();

    const opStatePromise = (async () => {
        const _opMap = (state as any).opStateMap || {};
        if (_opMap[lineId]) return _opMap[lineId];
        try {
            return await api(`/api/machines/${encodeURIComponent(lineId)}/operational-state`);
        } catch {
            return null;
        }
    })();

    const data = await api(`/api/line_detail/${lineId}`);

    const isTransformator = (lineId === 'TRANSFORMATOR');
    (window as any)._isTransformator = isTransformator;
    const trafoTabs = ['parts', 'conectare', 'consum', 'logs'];
    // «conectare» = config colectoare/registre — TOATE acțiunile sunt API master-only,
    // deci tabul se arată doar masterului real (audit 31 iul 2026: buton mort la inginer).
    // «docs» rămâne pentru inginer+ (upload documente e engineer-level).
    const masterOnlyTabs = ['conectare'];
    const operatorHiddenTabs = ['intervals', 'conectare', 'docs', 'extracts', 'consum', 'sensors', 'molds', 'hmi_mirror'];
    const conditionalTabs = ['hmi_mirror', 'sensors', 'etichete', 'imprimanta'];
    // 24 oct 2026 (Dan): la imprimarea etichetelor fila de tipărire e PRIMA și singura deschisă; filele de utilaj de
    // producție (revizia următoare, consum, proceduri, conectare) n-au sens la o imprimantă și o ascundeau pe telefon
    const estePrinterDetaliu = data?.machine?.machine_type === 'printer';
    const ascunseLaPrinter = ['parts', 'consum', 'extracts', 'conectare'];
    const isOperatorView = (window as any)._detailSource === 'operator';
    const esteMasterReal = state.currentUser?.role === 'master';
    $$('.detail-tab').forEach((t: Element) => {
        const he = t as HTMLElement;
        const dtab = he.dataset.dtab;
        if (conditionalTabs.includes(dtab || '')) return;
        if (estePrinterDetaliu && ascunseLaPrinter.includes(dtab || '')) { he.style.display = 'none'; return; }
        if (isTransformator) {
            he.style.display = trafoTabs.includes(dtab || '') && (dtab !== 'conectare' || esteMasterReal) ? '' : 'none';
        } else if (isOperatorView) {
            he.style.display = operatorHiddenTabs.includes(dtab || '') ? 'none' : '';
        } else {
            he.style.display = '';
            if (masterOnlyTabs.includes(dtab || '')) {
                he.style.display = esteMasterReal ? '' : 'none';
            }
        }
    });
    if (data.error) { ($('#detail-machine-info') as HTMLElement).innerHTML = `<div class="alarm-empty"><span class="icon">⚠</span>${escHtml(data.error)}</div>`; return; }
    const m = data.machine;
    const cfgs: any[] = (state.sensorConfigs || {})[lineId] || [];

    const isMoldDetail = (m.machine_type === 'mold');

    const cavitiesVal = (m.cavities != null && m.cavities !== '') ? parseInt(m.cavities, 10) : null;
    const moldLinkedHtml = isMoldDetail ? `
        <div class="detail-info-item"><span class="detail-info-label">Mașină legată</span><span class="detail-info-value">📎 ${escHtml(m.linked_machine_id || '—')}</span></div>
        ${cavitiesVal ? `<div class="detail-info-item"><span class="detail-info-label">Număr cuiburi</span><span class="detail-info-value">${cavitiesVal}</span></div>` : ''}
        <div class="detail-info-item"><span class="detail-info-label">Total Cicluri</span><span class="detail-info-value" style="font-family:var(--mono)">${((state.moldCounters || {})[lineId]?.cycles || 0).toLocaleString('ro-RO')}</span></div>
        <div class="detail-info-item"><span class="detail-info-label">Total Ore</span><span class="detail-info-value" style="font-family:var(--mono)">${((state.moldCounters || {})[lineId]?.hours || 0).toFixed(1)} h</span></div>
        <div class="detail-info-item"><span class="detail-info-label"><button class="btn btn-sm btn-outline" id="btn-reset-mold-counters" style="font-size:.65rem;padding:.15rem .5rem">Resetare contoare</button></span><span class="detail-info-value"></span></div>
    ` : '';

    const connProto = (m.connection_protocol || '').trim();
    const connProtoLabel = connProto ? (PROTO_LBL[connProto] || connProto) : '';
    const connStr = (m.connection_ip || connProtoLabel) ? [connProtoLabel, m.connection_ip, m.connection_port ? ':' + m.connection_port : ''].filter(Boolean).join(' ') : '';
    const connectionDisplay = connStr || '— Neconfigurat';
    const connectionHtml = `<div class="detail-info-item detail-info-item-conn detail-info-item-utilaj">
        <span class="detail-info-label">Conectare utilaj</span>
        <span class="detail-info-value detail-utilaj-value">${escHtml(connectionDisplay)}</span>
        <button type="button" class="detail-conn-configure btn-link" title="Deschide setările de conexiune">Configurează</button>
    </div>`;
    const mci = (m.meter_connection_ip || '').trim();
    const mcp = (m.meter_connection_port != null && m.meter_connection_port !== '') ? String(m.meter_connection_port).trim() : '';
    const meterProto = (m.meter_connection_protocol || '').trim();
    const meterProtoLabel = meterProto ? (PROTO_LBL[meterProto] || meterProto) : '';
    const meterDisplay = (mci || meterProtoLabel) ? [meterProtoLabel, mci, mcp ? ':' + mcp : ''].filter(Boolean).join(' ') || '— Neconfigurat' : '— Neconfigurat';
    const meterConnectionHtml = `<div class="detail-info-item detail-info-item-conn detail-info-item-meter">
        <span class="detail-info-label">Contor curent</span>
        <span class="detail-info-value detail-meter-value">${escHtml(meterDisplay)}</span>
        <button type="button" class="detail-conn-configure btn-link" title="Deschide setările de conexiune">Configurează</button>
    </div>`;
    const ac = m.assigned_counter;
    const counterDisplay = ac ? `${ac.name || 'Contor'} (${PROTO_LBL[ac.protocol] || ac.protocol})` : '— Nemonitorizat';
    const counterHtml = `<div class="detail-info-item"><span class="detail-info-label">Contor</span><span class="detail-info-value">${escHtml(counterDisplay)}</span></div>`;

    // Banner status (audit fix 2026-05-14):
    //  - Authoritative: backend status from /api/telemetry/sensors/hourly_avg → __lines__
    //    map (computed with the per-line `production_lines.power_threshold_kw`).
    //  - Else: fresh local `machine_state` from sensor_readings.
    //  - Else: fresh PAC3220 `power` with 1 kW fallback threshold.
    //  - Else: stale signal → "Necunoscut"; no telemetry at all → hide banner.
    const machineStateRawDetail = getSensorRaw(lineId, 'machine_state');
    const machineStateFreshDetail = machineStateRawDetail != null && isSensorRealtime(lineId, 'machine_state');
    const powerRawDetail = getSensorRaw(lineId, 'power') ?? getSensorRaw(lineId, 'consum');
    const powerFreshDetail = (getSensorRaw(lineId, 'power') != null && isSensorRealtime(lineId, 'power'))
                          || (getSensorRaw(lineId, 'consum') != null && isSensorRealtime(lineId, 'consum'));
    const lineAttr = getLineAttributedPower(lineId);

    let detailStateHtml = '';
    if (machineStateFreshDetail && machineStateRawDetail != null) {
        const stMap: Record<number, string> = {0:'Invalid',1:'Oprită',2:'Oprită intern',3:'Oprită downstream',4:'Oprită upstream',5:'Oprită servicii',6:'Oprită operator',7:'Producție',8:'Mentenanță',9:'Schimbare format',10:'Pre-încălzire'};
        const stNum = parseInt(String(machineStateRawDetail), 10);
        const stLabel = stMap[stNum] || `Stare ${machineStateRawDetail}`;
        const stClass = stNum === 7 ? 'producing' : (stNum >= 2 && stNum <= 6 ? 'stopped' : 'other');
        detailStateHtml = `<div class="detail-state-banner ${stClass}"><span class="eq-state-dot"></span>${escHtml(stLabel)}</div>`;
    } else if (lineAttr.status === 'running') {
        const pwTxt = lineAttr.power_kw_live != null ? lineAttr.power_kw_live.toFixed(2) : (lineAttr.power_kw != null ? lineAttr.power_kw.toFixed(2) : '?');
        detailStateHtml = `<div class="detail-state-banner producing" title="Linie ACTIVĂ (backend: ${pwTxt} kW peste pragul liniei)"><span class="eq-state-dot"></span>În producție</div>`;
    } else if (lineAttr.status === 'stopped') {
        const pwTxt = lineAttr.power_kw_live != null ? lineAttr.power_kw_live.toFixed(2) : (lineAttr.power_kw != null ? lineAttr.power_kw.toFixed(2) : '?');
        detailStateHtml = `<div class="detail-state-banner stopped" title="Linie OPRITĂ (backend: ${pwTxt} kW sub pragul liniei)"><span class="eq-state-dot"></span>Oprită</div>`;
    } else if (powerFreshDetail && powerRawDetail != null) {
        const pwNum = parseFloat(String(powerRawDetail));
        if (Number.isFinite(pwNum)) {
            if (pwNum >= 1) {
                detailStateHtml = `<div class="detail-state-banner producing" title="Putere ${pwNum.toFixed(2)} kW ≥ 1 kW (PAC3220 LIVE)"><span class="eq-state-dot"></span>În producție</div>`;
            } else {
                detailStateHtml = `<div class="detail-state-banner stopped" title="Putere ${pwNum.toFixed(2)} kW < 1 kW (PAC3220 LIVE)"><span class="eq-state-dot"></span>Oprită</div>`;
            }
        }
    } else if (machineStateRawDetail != null || powerRawDetail != null || lineAttr.status === 'unknown') {
        detailStateHtml = `<div class="detail-state-banner other" title="Senzor stare / contor curent fără date recente (>5 min)"><span class="eq-state-dot"></span>Necunoscut</div>`;
    }

    const machineAlarms = (state.controllerAlarms || []).filter((a: Alarm) => a.machine_id === lineId && !a.acked);
    let detailAlarmsHtml = '';
    if (machineAlarms.length > 0) {
        // 2026-07-05 (feedback Dan): lista ocupa jumătate de card și îngropa
        // restul informațiilor → pliată implicit, un singur rând sumar.
        const critN = machineAlarms.filter(a => a.severity === 'critical').length;
        const warnN = machineAlarms.filter(a => a.severity === 'warning').length;
        const sumBits = [
            critN ? `<b class="lp-crit">${critN} critice</b>` : '',
            warnN ? `<b class="lp-warn">${warnN} atenție</b>` : '',
        ].filter(Boolean).join(' · ');
        detailAlarmsHtml = `<details class="detail-active-alarms">
            <summary class="detail-alarms-title">🔔 Alarme active (${machineAlarms.length})${sumBits ? ' — ' + sumBits : ''} <span class="detail-alarms-hint">apasă pentru listă</span></summary>
            ${machineAlarms.map(a => `<div class="detail-alarm-row detail-alarm-clickable ${a.severity}" role="button" tabindex="0" data-machine-id="${escHtml(a.machine_id)}" data-alarm-code="${escHtml(a.alarm_code)}" data-alarm-type="${escHtml(a.alarm_type || '')}" title="Click pentru detalii din manual">
                <span class="detail-alarm-code">${escHtml(a.alarm_code)}</span>
                <span class="detail-alarm-desc">${escHtml(a.action)}</span>
                <span class="alarm-severity ${a.severity}">${a.severity.toUpperCase()}</span>
            </div>`).join('')}
        </details>`;
    }

    // Bug fix 2026-05-13: prefer backend-attributed line power (server-side
    // injected in /api/line_detail/<line_id> as `power_kw_attributed`) so
    // Line Detail shows the same value as Dashboard / Overview Fabrică.
    const attrPwrServer = (m as any).power_kw_attributed;
    const attrPwrLive = getLineAttributedPower(lineId);
    const attrPwr = (attrPwrLive.power_kw != null) ? attrPwrLive.power_kw : (typeof attrPwrServer === 'number' ? attrPwrServer : null);
    const attrStatus = attrPwrLive.status || (m as any).power_status || null;
    const avgPwr = getHourlyAvgPower(lineId);
    const pv = getSensorValue(lineId, 'power') ?? getSensorValue(lineId, 'consum');
    let consumVal: string;
    if (attrPwr != null) {
        consumVal = attrStatus === 'stopped'
            ? (attrPwr > 0.01
                ? attrPwr.toFixed(2) + ' kW (linie OPRITĂ — utilaje proprii)'
                : '0.00 kW (linie OPRITĂ)')
            : attrPwr.toFixed(2) + ' kW (medie 1h)';
    } else if (avgPwr != null) {
        consumVal = parseFloat(String(avgPwr)).toFixed(2) + ' kW (medie 1h)';
    } else if (pv != null) {
        consumVal = parseFloat(String(pv)).toFixed(2) + ' kW';
    } else if ((m.manual_power_kw || 0) > 0) {
        consumVal = parseFloat(String(m.manual_power_kw)).toFixed(2) + ' kW (manual)';
    } else {
        consumVal = '— kW';
    }
    const consumBanner = `<div class="detail-consum-banner"><span class="detail-consum-icon">⚡</span><span class="detail-consum-label">Consum</span><span class="detail-consum-val">${consumVal}</span></div>`;

    const opHours = parseFloat(String(m.operating_hours || 0));
    const opHoursOffset = parseFloat(String((m as any).operating_hours_offset || 0));
    const opHoursDisplay = opHours > 0 ? opHours.toFixed(1) + ' h' : '0 h — nesetat';
    const offsetTag = opHoursOffset > 0 ? `<span title="Cumulativ păstrat după reset ECU" style="margin-left:6px;padding:1px 6px;background:#f59e0b;color:#000;border-radius:6px;font-size:.65rem;font-weight:700">+${opHoursOffset.toFixed(1)}h offset</span>` : '';
    const hoursBanner = `<div class="detail-consum-banner" style="margin-top:4px">
        <span class="detail-consum-icon">⏱️</span>
        <span class="detail-consum-label">Ore funcționare</span>
        <span class="detail-consum-val" id="detail-ophours-val">${opHoursDisplay}</span>${offsetTag}
        <button type="button" class="btn btn-sm btn-outline" id="btn-edit-ophours" style="margin-left:8px;font-size:.65rem;padding:.15rem .5rem">Editează</button>
        <button type="button" class="btn btn-sm btn-outline" id="btn-graph-ophours" style="margin-left:4px;font-size:.65rem;padding:.15rem .5rem">📈 Grafic</button>
    </div>`;

    // Part 2 (2026-05-15) — Pauză tehnică: banner + listă task-uri sărite.
    // opStatePromise a pornit în paralel cu line_detail (nu blochează header-ul).
    let _techPauseBanner = '';
    try {
        let _opSt: any = await opStatePromise;
        if (!_opSt && m && m.id && m.id !== lineId) {
            const _opMap = (state as any).opStateMap || {};
            _opSt = _opMap[m.id] || null;
            if (!_opSt) {
                try {
                    _opSt = await api(`/api/machines/${encodeURIComponent(m.id)}/operational-state`);
                } catch {
                    _opSt = null;
                }
            }
        }
        if (_opSt && _opSt.is_technical_pause) {
            const _days = Math.floor((_opSt.offline_duration_hours || 0) / 24);
            const _dayStr = _days > 0 ? `${_days} ${_days === 1 ? 'zi' : 'zile'}` : `${Math.round(_opSt.offline_duration_hours)} h`;
            const _since = (_opSt.offline_since || '').replace('T', ' ').slice(0, 16);
            _techPauseBanner = `<div class="detail-state-banner detail-tech-pause" style="background:rgba(245,158,11,.15);border-left:4px solid #f59e0b;color:#fbbf24" title="Mentenanțele calendaristice cu skip_when_idle=1 sunt sărite automat. Workflow-urile în lucru rămân vizibile.">
                <span class="eq-state-dot" style="background:#f59e0b"></span>
                ⏸ <strong>PAUZĂ TEHNICĂ</strong> · oprită din ${escHtml(_since)} (${escHtml(_dayStr)})
                <span style="margin-left:8px;font-size:.7rem;opacity:.85">Mentenanțele calendaristice sunt sărite automat.</span>
            </div>`;
        }
    } catch (_) { /* defensiv */ }

    // Două benzi de stare una peste alta, care se contrazic: „Necunoscut" (senzor
    // fără citiri de peste 5 minute) chiar deasupra lui „PAUZĂ TEHNICĂ · oprită
    // din …". Dacă știm că e în pauză, starea NU e necunoscută — iar pe telefon
    // banda inutilă lua 26px din blocul de identitate, care oricum ajunsese la
    // 49% din ecran. Se păstrează banda de stare doar când chiar spune ceva.
    if (_techPauseBanner && detailStateHtml.includes('detail-state-banner other')) {
        detailStateHtml = '';
    }

    const isProductionMode = (window as any)._detailSource === 'operator';
    const commissioningHtml = isProductionMode ? '' : _renderCommissioningRow(m);
    ($('#detail-machine-info') as HTMLElement).innerHTML = `
        ${detailStateHtml}
        ${_techPauseBanner}
        ${detailAlarmsHtml}
        <details class="detail-info-fold"${window.innerWidth > 820 ? ' open' : ''}>
            <summary class="detail-info-fold-sum">Detalii tehnice și conectare</summary>
        <div class="detail-info-grid${isProductionMode ? ' detail-info-grid-operator' : ''}">
            ${isProductionMode ? '' : `<div class="detail-info-item"><span class="detail-info-label">Producător</span><span class="detail-info-value">${escHtml(m.manufacturer)}</span></div>
            <div class="detail-info-item"><span class="detail-info-label">Model</span><span class="detail-info-value">${escHtml(m.model)}</span></div>
            ${m.serial_number ? `<div class="detail-info-item"><span class="detail-info-label">S/N</span><span class="detail-info-value">${escHtml(m.serial_number)}</span></div>` : ''}`}
            ${commissioningHtml}
            ${isProductionMode ? '' : connectionHtml}
            ${isProductionMode ? '' : meterConnectionHtml}
            ${isProductionMode ? '' : counterHtml}
            ${m.status && m.status !== 'ok' ? `<div class="detail-info-item"><span class="detail-info-label">Status</span><span class="detail-info-value" style="color:${m.status==='stopped'?'var(--red)':'var(--yellow)'}">${m.status.toUpperCase()}${m.status_since ? ' din '+m.status_since : ''}${m.status_note ? ' — '+escHtml(m.status_note) : ''}</span></div>` : ''}
            ${isProductionMode ? '' : moldLinkedHtml}
        </div>
        </details>
        ${isProductionMode || m.machine_type === 'printer' ? '' : consumBanner}
        ${m.machine_type === 'printer' ? '' : isProductionMode ? `<div class="detail-consum-banner detail-operator-hours"><span class="detail-consum-icon">⏱️</span><span class="detail-consum-label">Ore funcționare</span><span class="detail-consum-val">${opHours > 0 ? opHours.toFixed(1) + ' h' : '0 h'}</span></div>` : hoursBanner}
    `;
    if (!isProductionMode) _enrichCommissioningRow(m.id || lineId);

    ($('#detail-machine-info') as HTMLElement).querySelectorAll('.detail-alarm-clickable').forEach((row: Element) => {
        const openAlarm = () => {
            const el = row as HTMLElement;
            const machineId = el.dataset.machineId || lineId;
            const alarmCode = el.dataset.alarmCode || '';
            if (!alarmCode) return;
            const alarm = machineAlarms.find(x => String(x.alarm_code) === alarmCode);
            openControllerAlarmDetailModal({
                machine_id: machineId,
                machine_name: m.name || machineId,
                alarm_code: alarmCode,
                alarm_type: alarm?.alarm_type || el.dataset.alarmType,
                severity: alarm?.severity,
                action: alarm?.action,
                cause_ro: alarm?.cause_ro,
                solution_ro: alarm?.solution_ro,
                par1: alarm?.par1,
                par2: alarm?.par2,
                created_at: alarm?.created_at,
                db_id: alarm?.db_id,
                title: '🔔 Alarmă activă',
                onHistory: () => { void openAlarmHistoryForMachine(machineId); },
                onSensors: () => { detailTabActive = 'sensors'; renderDetailTab(); },
                onMachine: () => { detailTabActive = 'parts'; renderDetailTab(); },
            });
        };
        row.addEventListener('click', openAlarm);
        row.addEventListener('keydown', (ev: Event) => {
            const ke = ev as KeyboardEvent;
            if (ke.key === 'Enter' || ke.key === ' ') { ev.preventDefault(); openAlarm(); }
        });
    });

    const configureConnBtn = document.querySelector('#detail-machine-info .detail-conn-configure');
    if (configureConnBtn) {
        configureConnBtn.addEventListener('click', () => {
            detailTabActive = 'conectare';
            document.querySelectorAll('.detail-tab').forEach((t: Element) => (t as HTMLElement).classList.toggle('active', (t as HTMLElement).dataset.dtab === 'conectare'));
            renderDetailTab();
        });
    }

    const editOpHoursBtn = document.getElementById('btn-edit-ophours');
    if (editOpHoursBtn && !isProductionMode) {
        editOpHoursBtn.addEventListener('click', async () => {
            const current = parseFloat(String(m.operating_hours || 0));
            const input = await askText(`Ore funcționare — ${m.name || lineId}`, {
                label: 'Citește valoarea de pe panoul HMI al mașinii.',
                value: String(current), inputMode: 'decimal', required: true,
            });
            if (input === null) return;
            const val = parseFloat(input);
            if (isNaN(val) || val < 0) { toast('Valoare invalidă', 'warning'); return; }
            // Refactor 2026-05-15: folosim endpoint-ul dedicat (PUT /api/machines/<id>/operating_hours)
            // care setează corect: operating_hours + offset + manual_hours_set_at +
            // auto_hours_source='manual' + last_running_check (sincron, nu via buffer).
            // Anterior se folosea /api/config/machines/<id>/counters (deprecated path) care
            // queue-uia într-un buffer in-memory și NU seta offset → telemetria suprascria.
            try {
                const r = await fetch(`/api/machines/${encodeURIComponent(lineId)}/operating_hours`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ operating_hours: val, reset_tracker: true, source: 'manual' }),
                });
                const data = await r.json().catch(() => ({}));
                if (!r.ok) {
                    alert(`Eroare salvare: ${(data && data.error) || r.status}`);
                    return;
                }
                const valEl = document.getElementById('detail-ophours-val');
                if (valEl) valEl.textContent = val.toFixed(1) + ' h';
                m.operating_hours = String(val);
                (m as any).auto_hours_source = 'manual';
                (m as any).manual_hours_set_at = (data && data.manual_set_at) || new Date().toISOString();
                alert(`Ore actualizate: ${val.toFixed(1)} h (manual — persistă)`);
            } catch (e) {
                alert('Eroare la salvare: ' + e);
            }
        });
    }

    const graphOpHoursBtn = document.getElementById('btn-graph-ophours');
    if (graphOpHoursBtn) {
        graphOpHoursBtn.addEventListener('click', async () => {
            await _showOperatingHoursGraph(lineId, m.name || lineId);
        });
    }

    (window as any)._detailData = data;
    (window as any)._detailLineId = lineId;
    (window as any)._revFilterInterval = '';
    (window as any)._ivFilterCategory = '';
    (window as any)._ivFilterIntervalText = '';
    (window as any)._logFilterOperator = '';

    const hmiTab = document.getElementById('detail-tab-hmi');
    const hmiUrl = (m.hmi_url || '').trim();
    if (hmiTab) hmiTab.style.display = hmiUrl ? '' : 'none';
    (window as any)._detailHmiUrl = hmiUrl;

    const sensorsTab = document.getElementById('detail-tab-sensors');
    if (sensorsTab) sensorsTab.style.display = cfgs.length > 0 ? '' : 'none';

    // Tab MATRIȚE: vizibil DOAR pentru mașini principale (injection/compression)
    const moldsTab = document.getElementById('detail-tab-molds');
    const isMainMachine = m && (m.machine_type === 'injection' || m.machine_type === 'compression');
    if (moldsTab) moldsTab.style.display = isMainMachine ? '' : 'none';

    // Tab ETICHETE (18 oct 2026): doar la utilajul de imprimare de pe linia de ambalare — și se deschide direct pe el.
    // Tab IMPRIMANTĂ (26 oct 2026): imprimantele Zebra din rețea (colectorul le citește pagina web) — la fel, prima filă.
    const eticheteTab = document.getElementById('detail-tab-etichete');
    const imprimantaTab = document.getElementById('detail-tab-imprimanta');
    const estePrinter = !!m && m.machine_type === 'printer';
    const esteZebra = estePrinter && (m.connection_protocol || '').startsWith('zebra');
    if (eticheteTab) eticheteTab.style.display = estePrinter && !esteZebra ? '' : 'none';
    if (imprimantaTab) imprimantaTab.style.display = esteZebra ? '' : 'none';

    const defaultTab = esteZebra ? 'imprimanta' : estePrinter ? 'etichete' : 'parts';
    detailTabActive = defaultTab;
    const barFile = eticheteTab?.parentElement;
    if (barFile) barFile.scrollLeft = 0;            // pe telefon rândul de file pornește de la prima (Etichete)
    $$('.detail-tab').forEach((t: Element) => (t as HTMLElement).classList.toggle('active', (t as HTMLElement).dataset.dtab === defaultTab));
    const tabLabelsOp: Record<string, string> = { parts: 'Revizii', errors: 'Defecte', logs: 'Istoric' };
    $$('.detail-tab').forEach((t: Element) => {
        const he = t as HTMLElement;
        const dtab = he.dataset.dtab || '';
        if (isOperatorView && tabLabelsOp[dtab]) he.textContent = tabLabelsOp[dtab];
        else if (dtab === 'parts') he.textContent = 'Următoarea revizie';
        else if (dtab === 'errors') he.textContent = 'Erori';
        else if (dtab === 'logs') he.textContent = 'Jurnal';
        else if (dtab === 'intervals') he.textContent = 'Intervale';
    });
    if (!isOperatorView) {
        const partsTab = document.querySelector('.detail-tab[data-dtab="parts"]');
        if (partsTab) partsTab.textContent = 'Următoarea revizie';
    }
    renderDetailTab();

    const resetBtn = document.getElementById('btn-reset-mold-counters');
    if (resetBtn) {
        resetBtn.addEventListener('click', async () => {
            if (!await askConfirm('Resetezi contoarele de cicluri și ore pentru această matriță?')) return;
            if (!state.moldCounters) state.moldCounters = {};
            state.moldCounters[lineId] = { cycles: 0, hours: 0 };
            state.sensorHistory[lineId] = {};
            localStorage.setItem('moldCounters', JSON.stringify(state.moldCounters));
            await fetch(`/api/config/machines/${encodeURIComponent(lineId)}/counters`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ cycle_count: 0, operating_hours: 0 }),
            });
            openLineDetail(lineId, product);
        });
    }

    // Header poze utilaj (dreapta sus, înainte de butoanele maximize/close).
    // Adăugat 2026-05-11: operatorii pot vedea pozele utilajului direct din modal.
    _renderMachineModalPhotos(lineId).catch(err => {
        console.warn('[machine-photos]', err);
    });
}

/* Inject thumbs poze utilaj în `.detail-header` (dreapta sus, lângă X/⛶).
 * Refacem container-ul la fiecare deschidere ca să nu rămână poze stale.
 */
async function _renderMachineModalPhotos(machineId: string): Promise<void> {
    const header = document.querySelector('#detail-overlay .detail-header') as HTMLElement | null;
    if (!header) return;
    // Curăță orice container vechi
    const old = header.querySelector('.machine-modal-photos');
    if (old) old.remove();

    const container = document.createElement('div');
    container.className = 'machine-modal-photos';
    container.setAttribute('aria-label', `Poze utilaj ${machineId}`);
    container.innerHTML = '<div class="machine-modal-photos-loading" aria-hidden="true"></div>';

    // Inserează container-ul ÎNAINTE de butoanele maximize/close (right side).
    const maxBtn = header.querySelector('#detail-maximize');
    if (maxBtn) header.insertBefore(container, maxBtn);
    else header.appendChild(container);

    let photos: LightboxPhoto[] = [];
    try {
        photos = await fetchMachinePhotos(machineId);
    } catch (_) { photos = []; }

    if (!photos || photos.length === 0) {
        container.innerHTML = `<div class="machine-modal-photos-empty" title="Fără poze. Adaugă din Configurare Fabrică.">
            <span class="mmp-empty-icon">📷</span>
            <span class="mmp-empty-text">Fără poze.</span>
            <a href="#" class="mmp-empty-link" data-mid="${escHtml(machineId)}">Adaugă din Configurare Fabrică</a>
        </div>`;
        const link = container.querySelector('.mmp-empty-link') as HTMLAnchorElement | null;
        if (link) {
            link.addEventListener('click', async (e) => {
                e.preventDefault();
                const w = window as any;
                try {
                    // Așteaptă să ai cache-ul de mașini (pentru openEditEquipModal nevoie de obiect eq)
                    await (w._ensureMachineCache ? w._ensureMachineCache() : Promise.resolve());
                    const all = (w._allMachinesCache || []) as any[];
                    const eq = all.find(m => m.id === machineId);
                    if (eq && typeof w.openEditEquipModal === 'function') {
                        await w.openEditEquipModal(eq);
                        // Focus pe butonul „Adaugă Poze" din modalul de editare
                        setTimeout(() => {
                            const addBtn = document.getElementById('btn-add-photo-edit');
                            if (addBtn) {
                                (addBtn as HTMLElement).focus();
                                addBtn.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }
                        }, 200);
                    } else if (typeof w.toast === 'function') {
                        w.toast('Mergi la Configurare Fabrică → Editează Utilaj → Adaugă Poze', 'info');
                    }
                } catch (err) {
                    console.warn('[machine-photos] open-edit', err);
                }
            });
        }
        return;
    }

    const MAX_VISIBLE = 3;
    const visible = photos.slice(0, MAX_VISIBLE);
    const extra = photos.length - visible.length;
    container.innerHTML = visible.map((p, i) =>
        `<button type="button" class="machine-modal-photo-thumb" data-idx="${i}" title="${escHtml(p.description || p.photo_type || 'Poză utilaj')}" aria-label="Vezi poza ${i + 1}">
            <img src="${escHtml(p.thumb_url || p.url)}" alt="${escHtml(p.description || 'Poză utilaj')}" loading="lazy" />
        </button>`
    ).join('') + (extra > 0
        ? `<button type="button" class="machine-modal-photo-thumb machine-modal-photo-more" data-idx="${MAX_VISIBLE}" title="Vezi toate (${photos.length} poze)" aria-label="Mai sunt ${extra} poze">+${extra}</button>`
        : ''
    );

    container.querySelectorAll('.machine-modal-photo-thumb').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            const idx = parseInt((btn as HTMLElement).dataset.idx || '0', 10) || 0;
            openMachineLightbox(photos, idx);
        });
    });
}

export async function renderDetailTab(): Promise<void> {
    const d = (window as any)._detailData as any;
    if (!d) return;
    const el: any = $('#detail-content');

    if (detailTabActive === 'parts') {
        const machineId = String((window as any)._detailLineId || d.machine?.id || '');
        const moldMap = state.moldCounters || {};
        const isMold = d.machine?.machine_type === 'mold';
        const hours = isMold ? (parseFloat(String(moldMap[machineId]?.hours)) || 0) : (parseFloat(String(state.machineHours[machineId])) || 0);
        const cycles = isMold ? (parseInt(String(moldMap[machineId]?.cycles), 10) || 0) : 0;
        const HOURS_7_DAYS = 168;
        const allIntervalsRaw = (d.intervals || []).slice();
        const disabledIntervals = allIntervalsRaw.filter((iv: any) => iv.enabled === 0);
        // 3 aug 2026 (raport Dan): în detaliul PROPRIEI mașini nu ascundem
        // intervalele după stare — bannerul de PAUZĂ TEHNICĂ de deasupra spune
        // deja situația; rândurile dispărute derutau („au fost șterse?").
        // 11 sep 2026: «procedura» = pașii unei lucrări periodice (natura_referinta) — nu e
        // scadență proprie (scadențarul o sare și el); se vede în tab-ul Intervale, la «Fel».
        const intervals = allIntervalsRaw.filter((iv: any) => iv.enabled !== 0 && iv.natura !== 'procedura');
        intervals.forEach((iv: any) => {
            // 1 nov 2026 (auditul surselor de ore): starea vine de pe server (`iv.stare`, routes/maintenance/_stare.py)
            // — lunarul pe calendar, ultima lucrare, orele de pe server; zilnicele sunt în planul zilei, nu aici
            if (!isMold && iv.stare) {
                const rem = oreRamaseStare(iv.stare);
                iv._hoursRemaining = iv.stare.mod === 'zilnic' ? null : rem;
                iv._hoursPast = iv.stare.ore_scurse ?? 0;
                iv._sortKey = iv._hoursRemaining == null ? 2e9 : iv._hoursRemaining;
            } else if ((iv.interval_hours || 0) > 0 && hours > 0) {
                const prog = intervalHoursProgress(iv, hours);
                iv._hoursRemaining = prog.overdue ? 0 : Math.max(0, prog.remaining);
                iv._hoursPast = prog.hoursSince;
                iv._sortKey = iv._hoursRemaining;
            } else if ((iv.interval_hours || 0) > 0 && hours === 0) {
                iv._hoursRemaining = null;
                iv._hoursPast = 0;
                iv._sortKey = 2e9;
            } else if (isMold && (iv.interval_cycles || 0) > 0 && cycles > 0) {
                const past = cycles % iv.interval_cycles;
                iv._cyclesRemaining = past === 0 ? 0 : iv.interval_cycles - past;
                iv._sortKey = 1e9 + (iv._cyclesRemaining || 0);
            } else {
                iv._sortKey = 2e9;
            }
        });
        intervals.sort((a: any, b: any) => (a._sortKey || 2e9) - (b._sortKey || 2e9));
        const withHours = intervals.filter((iv: any) => (iv.interval_hours || 0) > 0 && iv._hoursRemaining != null);
        const firstDueHours = Math.min(...withHours.map((iv: any) => iv._hoursRemaining ?? 1e9), 1e9);
        const inWindow = intervals.filter((iv: any) => {
            if ((iv.interval_hours || 0) > 0 && iv._hoursRemaining != null) return iv._hoursRemaining <= firstDueHours + HOURS_7_DAYS;
            if ((iv.interval_hours || 0) > 0 && iv._hoursRemaining == null) return false;
            if (isMold && (iv.interval_cycles || 0) > 0) return true;
            return false;
        });
        // Audit 2.15 — se cereau doar cele „în pauză". Workflow-ul 813 (M3,
        // interval 905, pornit 17 august) era `in_progress` de 39 de ore și nu
        // apărea nicăieri, deci alt mecanic putea demonta aceleași lame crezând
        // că nu s-a atins nimeni. Ruta face egalitate strictă pe status (o listă
        // ar întoarce 400), deci întrebăm de două ori.
        let activeWfs: any[] = [];
        try {
            const [inPauza, inCurs] = await Promise.all([
                api('/api/maintenance_workflows?machine_id=' + machineId + '&status=paused').catch(() => []),
                api('/api/maintenance_workflows?machine_id=' + machineId + '&status=in_progress').catch(() => []),
            ]);
            activeWfs = [...(inPauza || []), ...(inCurs || [])];
        } catch(e: any) {}
        const wfByInterval: Record<number, any> = {};
        (activeWfs || []).forEach((wf: any) => { if (wf.interval_id) wfByInterval[wf.interval_id] = wf; });

        const allIntervals = intervals;
        const uniqueIntervalTexts = [...new Set(allIntervals.map((iv: any) => iv.interval_text || '—'))].sort() as string[];
        const selectedInterval = (window as any)._revFilterInterval || '';

        const displayList = selectedInterval
            ? allIntervals.filter((iv: any) => (iv.interval_text || '—') === selectedInterval)
            : (inWindow.length > 0 ? inWindow : (hours === 0 ? allIntervals.filter((iv: any) => (iv.interval_hours || 0) > 0).slice(0, 20) : []));

        if (displayList.length === 0 && !selectedInterval) {
            const hasIntervals = allIntervals.length > 0;
            el.innerHTML = hasIntervals
                ? '<div class="alarm-empty"><span class="icon">⏱️</span>Selectează un interval din filtru sau setează orele de funcționare pe mașină pentru a vedea scadențele.</div>'
                : '<div class="alarm-empty"><span class="icon">📅</span>Niciun interval de mentenanță definit.</div>';
            return;
        }

        displayList.forEach((iv: any) => {
            const activeWf = wfByInterval[iv.id];
            if (activeWf) { iv._overdue = -1e6; }
            else if (!isMold && iv.stare) {
                iv._overdue = iv.stare.depasit ? 1e5 + Math.abs(iv.stare.zile || 0)
                    : (iv._hoursRemaining == null ? -2e9 : -iv._hoursRemaining);
            }
            else if (iv._hoursRemaining == null && (iv.interval_hours || 0) > 0) { iv._overdue = -2e9; }
            else if (isMold && (iv.interval_cycles || 0) > 0 && cycles > 0) {
                const past = cycles % iv.interval_cycles;
                iv._overdue = past === 0 ? iv.interval_cycles : -(iv.interval_cycles - past);
            } else if ((iv.interval_hours || 0) > 0 && hours > 0) {
                const prog = intervalHoursProgress(iv, hours);
                iv._overdue = prog.overdue ? iv.interval_hours : -Math.max(0, prog.remaining);
            } else { iv._overdue = -2e9; }
        });
        displayList.sort((a: any, b: any) => (b._overdue || 0) - (a._overdue || 0));

        state.lastRevisionList = displayList;

        const isOperatorView = (window as any)._detailSource === 'operator';
        if (isOperatorView) {
            const opList = displayList.slice(0, 6);
            let opHtml = '<div class="op-rev-cards">';
            opList.forEach((iv: any, idx: number) => {
                const activeWf = wfByInterval[iv.id];
                const isOverdue = (iv.stare ? !!iv.stare.depasit : (iv._overdue || 0) > 0) && !activeWf;
                const cardCls = isOverdue ? 'op-rev-card op-rev-overdue' : 'op-rev-card';
                const statusTxt = activeWf ? '⏸ În pauză'
                    : iv.stare ? `${isOverdue ? '🔴' : iv.stare.nivel === 'aproape' ? '🟡' : '🟢'} ${escHtml(iv.stare.text)}`
                    : isOverdue ? '🔴 Depășit' : '🟡 Scadent';
                const btnLabel = activeWf ? '▶ Continuă' : '▶ Începe';
                opHtml += `<div class="${cardCls}">
                    <div class="op-rev-card-head"><span class="op-rev-status">${statusTxt}</span><span class="op-rev-interval">${escHtml(iv.interval_text || '')}</span></div>
                    <div class="op-rev-action">${escHtml(truncate(iv.action_ro || '', 140))}</div>
                    <button type="button" class="btn btn-primary btn-sm op-rev-start" data-revision-index="${idx}" onclick="window.openRevisionDetailByIndex(${idx})">${btnLabel}</button>
                </div>`;
            });
            if (!opList.length) {
                opHtml += '<div class="alarm-empty"><span class="icon">✅</span>Nicio revizie scadentă în fereastra curentă</div>';
            } else if (displayList.length > 6) {
                opHtml += `<p class="op-more-hint">+ încă ${displayList.length - 6} intervale (vedere completă pentru inginer)</p>`;
            }
            opHtml += '</div>';
            el.innerHTML = opHtml;
            return;
        }

        let html = '<div class="detail-filter-bar">';
        html += '<label>Interval: <select id="rev-filter-interval"><option value="">Toate (fereastra 7 zile)</option>';
        uniqueIntervalTexts.forEach((t: string) => {
            html += `<option value="${escHtml(t)}"${t === selectedInterval ? ' selected' : ''}>${escHtml(t)}</option>`;
        });
        html += '</select></label></div>';

        const canTglRev = _canToggleMaintenanceIntervals();
        html += '<table class="detail-revision-table"><thead><tr><th>Interval</th><th>Acțiune</th><th>Categorie</th><th>Piesă/Lubrifiant</th><th>Manual</th><th>Status</th><th>Acțiuni</th></tr></thead><tbody>';
        displayList.forEach((iv: any, idx: number) => {
            let statusBadge = '<span class="badge badge-gray">N/A</span>';
            const activeWf = wfByInterval[iv.id];
            if (activeWf) {
                const pct = activeWf.steps_total > 0 ? Math.round((activeWf.steps_completed / activeWf.steps_total) * 100) : 0;
                statusBadge = `<span class="badge badge-yellow" title="Mentenanță în pauză: ${activeWf.steps_completed}/${activeWf.steps_total} pași">⏸ ÎN PAUZĂ ${pct}%</span>`;
            } else if (!isMold && iv.stare) {
                statusBadge = badgeStare(iv.stare);
            } else if (isMold && (iv.interval_cycles || 0) > 0 && cycles >= 0) {
                if (cycles === 0) {
                    statusBadge = '<span class="badge badge-gray">0 cicluri — fără date</span>';
                } else {
                    const past = cycles % iv.interval_cycles;
                    const rem = iv._cyclesRemaining ?? (past === 0 ? 0 : iv.interval_cycles - past);
                    const pct = iv.interval_cycles ? Math.round(past / iv.interval_cycles * 100) : 0;
                    if (past === 0) statusBadge = `<span class="badge badge-red">DEPĂȘIT — revizie la ${iv.interval_cycles.toLocaleString('ro-RO')} cicluri!</span>`;
                    else if (pct >= 90) statusBadge = `<span class="badge badge-red" title="${past.toLocaleString('ro-RO')}h parcurse din ${iv.interval_cycles.toLocaleString('ro-RO')}">DEPĂȘIT ${pct}% — ${Number(rem).toLocaleString('ro-RO')} cicluri rămase</span>`;
                    else if (pct >= 75) statusBadge = `<span class="badge badge-yellow">${pct}% — ${Number(rem).toLocaleString('ro-RO')} cicluri rămase</span>`;
                    else statusBadge = `<span class="badge badge-green">${pct}% — ${Number(rem).toLocaleString('ro-RO')} cicluri rămase</span>`;
                }
            } else if ((iv.interval_hours || 0) > 0 && hours >= 0) {
                if (hours === 0) {
                    const ih = iv.interval_hours;
                    const estDays = Math.ceil(ih / 24);
                    const label = ih <= 24 ? 'zilnic' : ih <= 168 ? `~${estDays} zile` : ih <= 750 ? 'lunar' : ih <= 2200 ? 'trimestrial' : ih <= 4500 ? 'semestrial' : 'anual+';
                    statusBadge = `<span class="badge badge-yellow" title="La ${ih}h funcționare (estimat ${estDays} zile la 24h/zi)">⏱ ${label} (${ih}h)</span>`;
                } else {
                    const prog = intervalHoursProgress(iv, hours);
                    const past = prog.hoursSince;
                    const rem = prog.overdue ? 0 : Math.max(0, prog.remaining);
                    const pct = iv.interval_hours ? Math.round(prog.ratio * 100) : 0;
                    if (prog.overdue) statusBadge = `<span class="badge badge-red" title="${Math.round(hours)}h total">DEPĂȘIT — revizie la ${iv.interval_hours}h!</span>`;
                    else if (pct >= 90) statusBadge = `<span class="badge badge-red" title="${Math.round(past)}h parcurse din ${iv.interval_hours}h">DEPĂȘIT ${pct}% — doar ${Math.round(rem)}h rămase (${Math.round(past)}h/${iv.interval_hours}h)</span>`;
                    else if (pct >= 75) statusBadge = `<span class="badge badge-yellow" title="${Math.round(past)}h/${iv.interval_hours}h">${pct}% — ${Math.round(rem)}h rămase</span>`;
                    else statusBadge = `<span class="badge badge-green" title="${Math.round(past)}h/${iv.interval_hours}h">${pct}% — ${Math.round(rem)}h rămase</span>`;
                }
            }
            let manualBtn = '';
            if (iv.document_id && iv.page_number) {
                manualBtn = `<a href="/api/documents/${iv.document_id}/view?page=${iv.page_number}" target="_blank" class="btn btn-sm" style="background:rgba(0,180,255,.08);border-color:rgba(0,180,255,.25);color:#00b4ff;text-decoration:none;white-space:nowrap">PDF p.${iv.page_number}</a>`;
                manualBtn += ` <button type="button" class="btn btn-xs iv-correct-page" data-iv-id="${iv.id}" data-current-page="${iv.page_number}" style="background:transparent;border:1px solid var(--border);color:var(--text-dim);font-size:.75em;cursor:pointer;padding:2px 6px" title="Corectează pagina">✏</button>`;
            } else if (iv.document_id) {
                manualBtn = `<a href="/api/documents/${iv.document_id}/view" target="_blank" class="btn btn-sm" style="background:rgba(0,180,255,.08);border-color:rgba(0,180,255,.25);color:#00b4ff;text-decoration:none;white-space:nowrap">PDF</a>`;
            }
            const btnLabel = activeWf ? '▶ Continuă revizia' : 'Detalii revizie';
            const btnClass = activeWf ? 'btn btn-sm btn-revision-detail btn-resume' : 'btn btn-sm btn-revision-detail';
            const revDisableBtn = canTglRev ? ` <button type="button" class="btn btn-xs iv-rev-disable" data-iv-idx="${idx}" style="background:rgba(239,83,80,.1);border:1px solid rgba(239,83,80,.3);color:#ef5350;cursor:pointer;padding:4px 8px;font-size:.75em" title="Dezactivează interval (nu se aplică pe această mașină)">✗</button>` : '';
            html += `<tr${activeWf ? ' class="wf-paused-row"' : ''}><td>${escHtml(iv.interval_text || '—')}</td><td>${escHtml(iv.action_ro || '')}</td><td><span class="badge badge-blue">${escHtml(iv.category || '')}</span></td><td>${escHtml(iv.part_number || iv.lubricant || '')}</td><td>${manualBtn}</td><td>${statusBadge}</td><td><button type="button" class="${btnClass}" data-revision-index="${idx}" onclick="window.openRevisionDetailByIndex(${idx})">${btnLabel}</button>${revDisableBtn}</td></tr>`;
        });
        html += '</tbody></table>';

        const canToggle = _canToggleMaintenanceIntervals();
        if (disabledIntervals.length > 0) {
            html += renderDisabledIntervalsSection(disabledIntervals, {
                summarySuffix: '(nu se aplică pe această mașină)',
            });
        }

        if (canToggle && displayList.length > 0) {
            html += '<p style="margin-top:.5em;font-size:.8em;color:var(--text-dim)">💡 Inginer/Master: click dreapta pe un rând din tabelul de mai sus pentru a dezactiva un interval inaplicabil.</p>';
        }

        el.innerHTML = html;

        if (canToggle) {
            el.querySelectorAll('.iv-toggle-enable').forEach((btn: Element) => {
                (btn as HTMLElement).addEventListener('click', async (e) => {
                    const ivId = (e.currentTarget as HTMLElement).dataset.ivId;
                    await api('/api/maintenance_intervals/' + ivId + '/toggle', {
                        method: 'POST', headers: {'Content-Type': 'application/json'},
                        body: JSON.stringify({enabled: true})
                    });
                    toast('Interval reactivat', 'success');
                    await _refreshDetailDataAndRender();
                });
            });

            const _askDisableInterval = async (iv: any) => {
                const reason = await askText('Dezactivare interval', {
                    label: (iv.action_ro || '').substring(0, 100) + '\n\nMotiv (ex: nu se aplică pe această configurație):',
                    placeholder: 'nu se aplică pe această configurație', okText: 'Dezactivează',
                });
                if (reason === null) return;
                await api('/api/maintenance_intervals/' + iv.id + '/toggle', {
                    method: 'POST', headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({enabled: false, reason: reason || 'dezactivat manual'})
                });
                toast('Interval dezactivat', 'success');
                await _refreshDetailDataAndRender();
            };
            // buton vizibil pe rând (tabletă) + click-dreapta ca scurtătură (desktop)
            el.querySelectorAll('.iv-rev-disable').forEach((btn: Element) => {
                (btn as HTMLElement).addEventListener('click', (e) => {
                    e.stopPropagation();
                    const iv = displayList[parseInt((btn as HTMLElement).dataset.ivIdx || '-1')];
                    if (iv) _askDisableInterval(iv);
                });
            });
            el.querySelectorAll('.detail-revision-table:first-of-type tbody tr').forEach((row: Element) => {
                (row as HTMLElement).addEventListener('contextmenu', (e) => {
                    const idx = Array.from(row.parentElement!.children).indexOf(row);
                    const iv = displayList[idx];
                    if (!iv) return;
                    e.preventDefault();
                    _askDisableInterval(iv);
                });
            });
        }

        document.getElementById('rev-filter-interval')?.addEventListener('change', (e) => {
            (window as any)._revFilterInterval = (e.target as HTMLSelectElement).value;
            renderDetailTab();
        });

        el.querySelectorAll('.iv-correct-page').forEach((btn: Element) => {
            (btn as HTMLElement).addEventListener('click', async (e) => {
                e.stopPropagation();
                const b = e.currentTarget as HTMLElement;
                const ivId = b.dataset.ivId;
                const currentPage = b.dataset.currentPage;
                const newPage = await askText('Corectare pagină manual', {
                    label: 'Pagina curentă: ' + currentPage + '\nIntroduceți pagina corectă:',
                    inputMode: 'numeric', okText: 'Corectează',
                });
                if (!newPage || isNaN(parseInt(newPage)) || parseInt(newPage) < 1) return;
                try {
                    const resp = await api('/api/maintenance_intervals/' + ivId + '/correct_page', {
                        method: 'POST',
                        headers: {'Content-Type': 'application/json'},
                        body: JSON.stringify({page: parseInt(newPage)})
                    });
                    if (resp.status === 'ok') {
                        toast('Pagina corectată: ' + currentPage + ' → ' + newPage, 'success');
                        renderDetailTab();
                    } else {
                        toast(resp.error || 'Eroare', 'error');
                    }
                } catch (ex) {
                    toast('Eroare conexiune', 'error');
                }
            });
        });
    } else if (detailTabActive === 'intervals') {
        const enabledIvs = (d.intervals || []).filter((iv: any) => iv.enabled !== 0);
        const disabledIvs = (d.intervals || []).filter((iv: any) => iv.enabled === 0);
        if (enabledIvs.length === 0 && disabledIvs.length === 0) {
            el.innerHTML = '<div class="alarm-empty"><span class="icon">📅</span>Niciun interval definit</div>';
            renderCupluriSection(el, String(d.machine.id));
            return;
        }
        const moldId = String((window as any)._detailLineId || (d.machine && d.machine.id) || '');
        const moldMapIv = state.moldCounters || {};
        const isMold = d.machine.machine_type === 'mold';
        const hours = isMold ? (parseFloat(String(moldMapIv[moldId]?.hours)) || 0) : (parseFloat(String(state.machineHours[moldId])) || 0);
        const cycles = isMold ? (parseInt(String(moldMapIv[moldId]?.cycles), 10) || 0) : 0;

        const allCategories = [...new Set(enabledIvs.map((iv: any) => iv.category || '').filter(Boolean))].sort() as string[];
        const allIntervalTexts = [...new Set(enabledIvs.map((iv: any) => iv.interval_text || '—'))].sort() as string[];
        const selCat = (window as any)._ivFilterCategory || '';
        const selIvText = (window as any)._ivFilterIntervalText || '';
        // 11 sep 2026: «Fel» = natura lucrării (determinist, din manual): periodică / la
        // condiție / punere în funcțiune / la eveniment / reglaj / cuplu de strângere /
        // procedură (pașii unei lucrări periodice, natura_referinta = lucrarea explicată)
        const selNatura = (window as any)._ivFilterNatura || '';
        const naturaOf = (iv: any) => (iv.natura || 'periodica') as string;
        const allNaturi = ([...new Set(enabledIvs.map(naturaOf))] as string[]).sort((a: string, b: string) => NATURA_ORDINE.indexOf(a) - NATURA_ORDINE.indexOf(b));
        let filtered = selCat ? enabledIvs.filter((iv: any) => (iv.category || '') === selCat) : enabledIvs;
        if (selIvText) filtered = filtered.filter((iv: any) => (iv.interval_text || '—') === selIvText);
        if (selNatura) filtered = filtered.filter((iv: any) => naturaOf(iv) === selNatura);
        const byId: Record<string, any> = {};
        (d.intervals || []).forEach((iv: any) => { byId[String(iv.id)] = iv; });

        let html = '<div class="detail-filter-bar"><label>Categorie: <select id="iv-filter-cat"><option value="">Toate</option>';
        allCategories.forEach((c: string) => { html += `<option value="${escHtml(c)}"${c === selCat ? ' selected' : ''}>${escHtml(c)}</option>`; });
        html += '</select></label>';
        // 3 aug 2026 (cerința Dan): filtru și pe INTERVAL (zilnic/lunar/la 180h…),
        // nu doar pe categorie — combinabile
        html += ' <label>Interval: <select id="iv-filter-ivtext"><option value="">Toate</option>';
        allIntervalTexts.forEach((t: string) => { html += `<option value="${escHtml(t)}"${t === selIvText ? ' selected' : ''}>${escHtml(t)}</option>`; });
        html += '</select></label>';
        html += ' <label>Fel: <select id="iv-filter-natura"><option value="">Toate</option>';
        allNaturi.forEach((n: string) => {
            const cnt = enabledIvs.filter((iv: any) => naturaOf(iv) === n).length;
            html += `<option value="${escHtml(n)}"${n === selNatura ? ' selected' : ''}>${escHtml(NATURA_ETICHETE[n] || n)} (${cnt})</option>`;
        });
        html += '</select></label></div>';

        html += '<table><thead><tr><th>Interval</th><th>Fel</th><th>Acțiune</th><th>Categorie</th><th>Piesă/Lubrifiant</th><th>Manual</th><th>Status</th></tr></thead><tbody>';
        filtered.forEach((iv: any) => {
            const nat = naturaOf(iv);
            let felCell = `<span class="badge ${nat === 'periodica' ? 'badge-blue' : 'badge-gray'}" title="${escHtml(iv.natura_motiv || '')}">${escHtml(NATURA_ETICHETE[nat] || nat)}</span>`;
            if (nat === 'procedura' && iv.natura_referinta) {
                const ref = byId[String(iv.natura_referinta)];
                felCell += `<div style="font-size:.72em;opacity:.8;margin-top:2px" title="${escHtml(ref ? (ref.action_ro || '') : '')}">pașii lucrării #${escHtml(String(iv.natura_referinta))}${ref ? ' — ' + escHtml(truncate(ref.action_ro || '', 40)) : ''}</div>`;
            }
            let statusBadge = nat === 'periodica' || nat === 'nedeterminata'
                ? '<span class="badge badge-gray">N/A</span>'
                : `<span class="badge badge-gray" title="${escHtml(iv.natura_motiv || '')}">${nat === 'procedura' ? 'vezi lucrarea' : 'fără scadență — ' + escHtml(NATURA_ETICHETE[nat] || nat).toLowerCase()}</span>`;
            if (nat === 'procedura') {
                // pașii unei lucrări periodice: scadența e a lucrării, nu a pașilor
            } else if (!isMold && iv.stare && iv.stare.mod !== 'eveniment' && (nat === 'periodica' || nat === 'nedeterminata')) {
                // 1 nov 2026 (auditul surselor de ore): starea din sursa unică — aceeași cu „Următoarea revizie”,
                // „Ce urmează”, panourile și Restanțele (înainte: `ore % interval`, apoi socotită în pagină)
                statusBadge = badgeStare(iv.stare);
            } else
            if (isMold && iv.interval_cycles && cycles >= 0) {
                if (cycles === 0) {
                    statusBadge = '<span class="badge badge-gray">0 cicluri — fără date</span>';
                } else {
                    const past = cycles % iv.interval_cycles;
                    const rem = past === 0 ? 0 : iv.interval_cycles - past;
                    const pct = Math.round(past / iv.interval_cycles * 100);
                    if (past === 0) statusBadge = `<span class="badge badge-red">DEPĂȘIT — revizie la ${iv.interval_cycles.toLocaleString('ro-RO')} cicluri!</span>`;
                    else if (pct >= 90) statusBadge = `<span class="badge badge-red">DEPĂȘIT ${pct}% — ${rem.toLocaleString('ro-RO')} cicluri rămase</span>`;
                    else if (pct >= 75) statusBadge = `<span class="badge badge-yellow">${pct}% — ${rem.toLocaleString('ro-RO')} cicluri rămase</span>`;
                    else statusBadge = `<span class="badge badge-green">${pct}% — ${rem.toLocaleString('ro-RO')} cicluri rămase</span>`;
                }
            } else if (iv.interval_hours && iv.interval_hours > 0) {
                if (hours === 0) {
                    const ih2 = iv.interval_hours;
                    const estDays2 = Math.ceil(ih2 / 24);
                    const label2 = ih2 <= 24 ? 'zilnic' : ih2 <= 168 ? `~${estDays2} zile` : ih2 <= 750 ? 'lunar' : ih2 <= 2200 ? 'trimestrial' : ih2 <= 4500 ? 'semestrial' : 'anual+';
                    statusBadge = `<span class="badge badge-yellow" title="La ${ih2}h funcționare (estimat ${estDays2} zile la 24h/zi)">⏱ ${label2} (${ih2}h)</span>`;
                } else {
                    // rezervă, doar fără `iv.stare` (răspuns vechi în cache): de la ultima lucrare, nu `ore % interval`
                    {
                        const prog = intervalHoursProgress(iv, hours);
                        const past = Math.round(prog.hoursSince), rem = Math.round(prog.remaining);
                        const pct = Math.round(prog.ratio * 100);
                        if (prog.overdue) statusBadge = `<span class="badge badge-red">DEPĂȘIT ${pct}% (${past}h/${iv.interval_hours}h)</span>`;
                        else if (pct >= 90) statusBadge = `<span class="badge badge-yellow">${pct}% — ${rem}h rămase</span>`;
                        else statusBadge = `<span class="badge badge-green">${pct}% — ${rem}h rămase</span>`;
                    }
                }
            }
            let manualBtnIv = '';
            if (iv.document_id && iv.page_number) {
                manualBtnIv = `<a href="/api/documents/${iv.document_id}/view?page=${iv.page_number}" target="_blank" class="btn btn-sm" style="background:rgba(0,180,255,.08);border-color:rgba(0,180,255,.25);color:#00b4ff;text-decoration:none;white-space:nowrap">PDF p.${iv.page_number}</a>`;
            } else if (iv.document_id) {
                manualBtnIv = `<a href="/api/documents/${iv.document_id}/view" target="_blank" class="btn btn-sm" style="background:rgba(0,180,255,.08);border-color:rgba(0,180,255,.25);color:#00b4ff;text-decoration:none;white-space:nowrap">PDF</a>`;
            }
            const canTgl = state.currentUser && (state.currentUser.role === 'engineer' || state.currentUser.role === 'master');
            const disableBtn = canTgl ? ` <button type="button" class="btn btn-xs iv-toggle-disable" data-iv-id="${iv.id}" style="background:rgba(239,83,80,.1);border:1px solid rgba(239,83,80,.3);color:#ef5350;cursor:pointer;padding:1px 6px;font-size:.7em" title="Dezactivează interval">✗</button>` : '';
            html += `<tr><td>${escHtml(iv.interval_text)}</td><td>${felCell}</td><td>${escHtml(iv.action_ro)}</td><td><span class="badge badge-blue">${escHtml(iv.category)}</span></td><td>${escHtml(iv.part_number || iv.lubricant || '')}</td><td>${manualBtnIv}</td><td>${statusBadge}${disableBtn}</td></tr>`;
        });
        html += '</tbody></table>';

        if (disabledIvs.length > 0) {
            html += renderDisabledIntervalsSection(disabledIvs);
        }

        el.innerHTML = html;
        renderCupluriSection(el, String(d.machine.id));

        el.querySelectorAll('.iv-toggle-disable').forEach((btn: Element) => {
            (btn as HTMLElement).addEventListener('click', async (e) => {
                e.stopPropagation();
                const ivId = (e.currentTarget as HTMLElement).dataset.ivId;
                const reason = await askText('Dezactivare interval', {
                    label: 'Motiv (ex: nu se aplică pe această configurație):',
                    placeholder: 'nu se aplică pe această configurație', okText: 'Dezactivează',
                });
                if (reason === null) return;
                await api('/api/maintenance_intervals/' + ivId + '/toggle', {
                    method: 'POST', headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({enabled: false, reason: reason || 'dezactivat manual'})
                });
                toast('Interval dezactivat', 'success');
                await _refreshDetailDataAndRender();
            });
        });
        el.querySelectorAll('.iv-toggle-enable').forEach((btn: Element) => {
            (btn as HTMLElement).addEventListener('click', async (e) => {
                const ivId = (e.currentTarget as HTMLElement).dataset.ivId;
                await api('/api/maintenance_intervals/' + ivId + '/toggle', {
                    method: 'POST', headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({enabled: true})
                });
                toast('Interval reactivat', 'success');
                await _refreshDetailDataAndRender();
            });
        });

        document.getElementById('iv-filter-cat')?.addEventListener('change', (e) => {
            (window as any)._ivFilterCategory = (e.target as HTMLSelectElement).value;
            renderDetailTab();
        });
        document.getElementById('iv-filter-ivtext')?.addEventListener('change', (e) => {
            (window as any)._ivFilterIntervalText = (e.target as HTMLSelectElement).value;
            renderDetailTab();
        });
        document.getElementById('iv-filter-natura')?.addEventListener('change', (e) => {
            (window as any)._ivFilterNatura = (e.target as HTMLSelectElement).value;
            renderDetailTab();
        });
    } else if (detailTabActive === 'errors') {
        if (d.errors.length === 0) {
            el.innerHTML = '<div class="alarm-empty"><span class="icon">⚠</span>Niciun cod de eroare</div>';
            return;
        }
        let html = `<div class="results-info">${d.errors_total} coduri total (afișate ${d.errors.length})</div>`;
        html += '<table><thead><tr><th>Cod</th><th>Descriere</th><th>Cauză</th><th>Soluție</th><th></th></tr></thead><tbody>';
        d.errors.forEach((e: any) => {
            let manualBtn = '';
            if (e.chapter_id) {
                manualBtn = `<button type="button" class="btn btn-sm error-see-manual-btn" data-chapter-id="${e.chapter_id}" data-doc-id="${e.document_id}" style="background:rgba(0,180,255,.12);border-color:rgba(0,180,255,.35);color:#00b4ff;white-space:nowrap">Vezi in manual</button>`;
            } else if (e.document_id) {
                const doc = (d.documents || []).find((dc: any) => dc.id === e.document_id);
                if (doc && doc.filename) {
                    manualBtn = `<a href="/uploads/${doc.filename}" target="_blank" class="btn btn-sm" style="background:rgba(0,180,255,.08);border-color:rgba(0,180,255,.25);color:#00b4ff;text-decoration:none;white-space:nowrap">PDF</a>`;
                }
            }
            html += `<tr><td><code>${escHtml(e.error_code)}</code></td><td>${escHtml(truncate(e.description_ro, 80))}</td><td>${escHtml(truncate(e.probable_cause, 80))}</td><td>${escHtml(truncate(e.solution, 80))}</td><td>${manualBtn}</td></tr>`;
        });
        html += '</tbody></table>';
        el.innerHTML = html;
        el.querySelectorAll('.error-see-manual-btn').forEach((btn: Element) => {
            const hb = btn as HTMLElement;
            hb.addEventListener('click', async () => {
                const chId = hb.dataset.chapterId;
                const docId = hb.dataset.docId;
                try {
                    const res = await fetch(`/api/documents/${docId}/chapter/${chId}/pdf`);
                    const data = await res.json();
                    if (res.ok && data.url) {
                        window.open(data.url, '_blank');
                    }
                } catch(err: any) { toast('Eroare: ' + err.message, 'error'); }
            });
        });
    } else if (detailTabActive === 'docs') {
        const machineId = (window as any)._detailLineId || (d.machine && d.machine.id);
        let html = '';

        // SACMI Space quick-access button
        try {
            const sacmiData = await fetch(`/api/machines/${encodeURIComponent(machineId)}/sacmi_links`, {credentials:'include'}).then(r => r.json());
            if (sacmiData && sacmiData.equipment_id) {
                const catalogCode = sacmiData.catalog_code || '';
                const equipId = sacmiData.equipment_id || '';
                const esparesUrl = catalogCode && equipId
                    ? `https://sacmi.space/ESpares/Catalog/Index/${catalogCode}?equipment=${equipId}` : '';
                const edocsUrl = 'https://sacmi.space/EDocs/Docs/List';

                html += `<div style="display:flex;gap:8px;margin-bottom:12px;flex-wrap:wrap;align-items:center">
                    <span style="font-size:.85rem;color:var(--text-muted)">SACMI Space:</span>
                    ${esparesUrl ? `<a href="${esparesUrl}" target="_blank" rel="noopener" class="btn btn-sm" style="background:rgba(220,38,38,.1);border-color:rgba(220,38,38,.3);color:#ef4444">🔍 Catalog Piese Interactiv</a>` : ''}
                    <a href="${edocsUrl}" target="_blank" rel="noopener" class="btn btn-sm" style="background:rgba(220,38,38,.1);border-color:rgba(220,38,38,.3);color:#ef4444">📄 E-Docs</a>
                </div>`;
            }
        } catch {}

        // Template navigator & completeness
        if (d.template && d.template_sections && d.template_sections.length > 0) {
            html += _renderDocumentTemplateNavigator(d);
        }

        if (d.documents.length === 0 && !(d.template && d.template_sections && d.template_sections.length > 0)) {
            html += '<div class="alarm-empty"><span class="icon">📄</span>Niciun document. Adaugă mai jos.</div>';
        } else if (d.documents.length === 0) {
            // template exists but no documents yet
        } else {
            const isMaster = (state.currentUser && state.currentUser.role === 'master');
            if (isMaster) {
                const hasProcessed = d.documents.some((dc: any) => dc.processed === 1 || dc.processed === true);
                if (hasProcessed) {
                    html += `<div style="margin-bottom:10px;display:flex;align-items:center;gap:10px;flex-wrap:wrap">
                        <button type="button" class="btn btn-sm" id="btn-analyze-all-imgs" style="background:rgba(0,180,255,.15);border-color:rgba(0,180,255,.4);color:#00d4ff" title="Analizează cu AI Vision toate imaginile din documentele procesate">🖼️🔍 AI Vision imagini</button>
                        <button type="button" class="btn btn-sm" id="btn-fix-page-ranges" style="background:rgba(16,185,129,.12);border-color:rgba(16,185,129,.35);color:#10b981" title="Corelează imaginile cu procedurile (populează paginile sursă pentru extract-uri existente)">📄🔗 Corelează imagini-proceduri</button>
                        <span id="analyze-all-status" style="font-size:.85rem;color:var(--text-muted)"></span>
                    </div>`;
                }
            }
            html += '<table><thead><tr><th>Tip</th><th>Fișier</th><th>Limbă</th><th>Pagini</th><th>Descriere</th><th>Status</th><th>Data</th><th>Acțiuni</th></tr></thead><tbody>';
            d.documents.forEach((dc: any) => {
                const processed = dc.processed === 1 || dc.processed === true;
                const ext = (dc.filename || '').split('.').pop().toLowerCase();
                // Word/Excel/poze: procesarea le face întâi PDF (doc nou), deci se pot procesa; reprocesarea rămâne pe PDF
                const canProcess = isMaster && !processed && ['pdf', 'docx', 'xlsx', 'png', 'jpg', 'jpeg'].includes(ext);
                const canReprocess = isMaster && processed && ext === 'pdf';
                const statusId = `doc-status-${dc.id}`;

                const pStatus = dc.processing_status || (processed ? 'processed' : 'unprocessed');
                const statusLabels: Record<string, string> = {
                    'unprocessed': '<span class="doc-status-tag doc-status-unprocessed">Neprocesat</span>',
                    'processing':  '<span class="doc-status-tag doc-status-processing">În procesare…</span>',
                    'processed':   '<span class="doc-status-tag doc-status-processed">Procesat</span>',
                    'reprocessed': '<span class="doc-status-tag doc-status-reprocessed">Reprocesat</span>',
                    'error':       '<span class="doc-status-tag doc-status-error">Eroare</span>'
                };
                let statusCell = `<span class="doc-status-inline" id="${statusId}" data-doc-id="${dc.id}"></span>`;
                statusCell += statusLabels[pStatus] || statusLabels['unprocessed'];

                let processedAtCell = '—';
                if (dc.processed_at) {
                    const dt = new Date(dc.processed_at + 'Z');
                    processedAtCell = dt.toLocaleDateString('ro-RO', {day:'2-digit',month:'2-digit',year:'numeric'})
                        + ' ' + dt.toLocaleTimeString('ro-RO', {hour:'2-digit',minute:'2-digit'});
                }

                let actionsCell = '';
                if (processed && ext === 'pdf') {
                    actionsCell += `<button type="button" class="btn btn-sm doc-detect-chapters-btn" data-doc-id="${dc.id}" style="background:rgba(16,185,129,.12);border-color:rgba(16,185,129,.35);color:#10b981" title="Detectează capitolele din PDF">Capitole</button> `;
                }
                if (processed && canReprocess) {
                    actionsCell += `<button type="button" class="btn btn-sm doc-reprocess-btn" data-doc-id="${dc.id}" title="Reprocesează cu prompt îmbunătățit">Reprocesează</button>`;
                } else if (canProcess) {
                    actionsCell += `<button type="button" class="btn btn-sm doc-abandon-btn" data-doc-id="${dc.id}" title="Eliberează progres blocat">Eliberează</button> `;
                    actionsCell += `<button type="button" class="btn btn-sm doc-process-btn" data-doc-id="${dc.id}" title="Extrage intervale și piese din document">Procesează</button>`;
                }
                if (isMaster && dc.filename && dc.filename.toLowerCase().endsWith('.pdf') && processed) {
                    actionsCell += `<button type="button" class="btn btn-sm doc-analyze-imgs-btn" data-doc-id="${dc.id}" title="Analizează imaginile din acest document cu AI Vision">🖼️ Vision</button>`;
                }
                if (isMaster && pStatus !== 'processing') {
                    actionsCell += ` <button type="button" class="btn btn-sm doc-delete-btn" data-doc-id="${dc.id}" data-doc-name="${escHtml(dc.filename)}" title="Șterge documentul și toate datele generate">Șterge</button>`;
                }

                const langFlags: Record<string, string> = {ro:'🇷🇴',en:'🇬🇧',de:'🇩🇪',it:'🇮🇹',fr:'🇫🇷',hu:'🇭🇺'};
                const langDisplay = (langFlags[dc.language] || '🌐') + ' ' + (dc.language || 'auto').toUpperCase();
                html += `<tr><td><span class="badge badge-blue">${escHtml(dc.doc_type)}</span></td><td>${escHtml(dc.filename)}</td><td>${langDisplay}</td><td>${dc.pages || '—'}</td><td>${escHtml(truncate(dc.description, 60))}</td><td>${statusCell}</td><td>${processedAtCell}</td><td class="doc-actions-cell">${actionsCell}</td></tr>`;
            });
            html += '</tbody></table>';
        }
        html += `<p class="doc-process-hint">Documentele PDF pot fi procesate automat: se extrag intervale de mentenanță, piese, proceduri + imagini analizate cu AI Vision.</p>

          <div class="doc-upload-section">
            <h4 class="doc-upload-title">Adaugă document</h4>
            <form id="doc-upload-form" class="doc-upload-form">
              <input type="hidden" name="machine_id" value="${escHtml(machineId || '')}">`;

        const tplType = d.template ? d.template.template_type : null;
        const tplSections = d.template_sections || [];
        if (tplType === 'categories') {
            const catOptions = tplSections.filter((s: any) => !s.parent_id).map((s: any) =>
                `<option value="${s.id}">${escHtml(s.title)}</option>`
            ).join('');
            html += `<div class="form-row">
                <label>Categorie: <select name="template_section_id" required>${catOptions}</select></label>
                <label>Limbă: <select name="language" id="doc-upload-lang">
                  <option value="auto">Auto-detect</option>
                  <option value="ro">RO - Română</option><option value="en">EN - English</option>
                  <option value="de">DE - Deutsch</option><option value="it">IT - Italiano</option>
                  <option value="fr">FR - Français</option><option value="hu">HU - Magyar</option>
                </select></label>
              </div>
              <div class="form-row">
                <label>Descriere: <input type="text" name="description" placeholder="Descriere document (obligatoriu)" required></label>
              </div>
              <div class="form-row">
                <label>Document Code: <input type="text" name="doc_code" placeholder="Cod document SACMI (optional)" style="max-width:200px"></label>
                <input type="hidden" name="doc_type" value="document">
              </div>`;
        } else {
            const secOpts = _buildTreeSectionOptions(tplSections, true);
            html += `<div class="form-row">
                <label>Tip document: <select name="doc_type" required>
                  <option value="Manual utilizare">Manual utilizare</option>
                  <option value="Carte tehnică">Carte tehnică</option>
                  <option value="Scheme electrice">Scheme electrice</option>
                  <option value="Catalog piese">Catalog piese</option>
                  <option value="Fișă tehnică">Fișă tehnică</option>
                  <option value="document">Altul</option>
                </select></label>
                <label>Limbă: <select name="language" id="doc-upload-lang">
                  <option value="auto">Auto-detect</option>
                  <option value="ro">RO - Română</option><option value="en">EN - English</option>
                  <option value="de">DE - Deutsch</option><option value="it">IT - Italiano</option>
                  <option value="fr">FR - Français</option><option value="hu">HU - Magyar</option>
                </select></label>
              </div>
              <div class="form-row">
                <label>Sectiune destinatie: <select name="template_section_id">${secOpts}</select></label>
              </div>
              <div class="form-row">
                <label>Descriere: <input type="text" name="description" placeholder="Opțional"></label>
              </div>`;
        }

        html += `<div class="form-row">
                <label>Fișiere (PDF, DOC, XLS, imagini): <input type="file" name="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg" required multiple></label>
                <span id="doc-upload-file-count" style="font-size:.8rem;color:var(--text-muted)"></span>
              </div>
              <button type="submit" class="btn btn-primary">Încarcă</button>
            </form>
          </div>`;
        el.innerHTML = html;
        // Tree expand/collapse
        el.querySelectorAll('.doc-tree-item.has-children > .doc-tree-row').forEach((row: Element) => {
            row.addEventListener('click', () => {
                const item = row.parentElement;
                if (!item) return;
                const children = item.querySelector('.doc-tree-children') as HTMLElement | null;
                const expand = item.querySelector('.doc-tree-expand') as HTMLElement | null;
                if (children) {
                    const isOpen = children.style.display !== 'none';
                    children.style.display = isOpen ? 'none' : 'block';
                    if (expand) expand.classList.toggle('open', !isOpen);
                }
            });
        });
        // Page link click -> open PDF
        el.querySelectorAll('.doc-tree-page').forEach((pg: Element) => {
            pg.addEventListener('click', (e: Event) => {
                e.stopPropagation();
                const docId = (pg as HTMLElement).dataset.docId;
                const page = (pg as HTMLElement).dataset.page;
                const doc = d.documents.find((dc: any) => String(dc.id) === String(docId));
                if (doc && doc.filename) {
                    window.open(`/uploads/${doc.filename}#page=${page}`, '_blank');
                }
            });
        });
        // Delete chapter
        el.querySelectorAll('.doc-ch-delete').forEach((btn: Element) => {
            btn.addEventListener('click', async (e: Event) => {
                e.stopPropagation();
                const hb = btn as HTMLElement;
                const mdId = hb.dataset.mdId;
                const chId = hb.dataset.chId;
                const docId = hb.dataset.docId;
                if (mdId) {
                    if (!await askConfirm('Stergi alocarea documentului de la aceasta sectiune?\n(Documentul ramane in baza de date)')) return;
                    try {
                        const res = await fetch(`/api/machine-documents/${mdId}`, {method:'DELETE', credentials:'include'});
                        if (!res.ok) { const d = await res.json().catch(()=>({})); toast(d.error || 'Eroare la stergere', 'error'); return; }
                        toast('Alocare stearsa');
                        if ((window as any)._detailLineId) {
                            const fresh = await api(`/api/line_detail/${(window as any)._detailLineId}`);
                            (window as any)._detailData = fresh;
                            renderDetailTab();
                        }
                    } catch(err: any) { toast('Eroare: ' + err.message, 'error'); }
                } else if (chId && docId) {
                    if (!await askConfirm('Stergi acest capitol detectat?')) return;
                    try {
                        await fetch(`/api/documents/${docId}/chapters/${chId}`, {method:'DELETE', credentials:'include'});
                        toast('Capitol sters');
                        if ((window as any)._detailLineId) {
                            const fresh = await api(`/api/line_detail/${(window as any)._detailLineId}`);
                            (window as any)._detailData = fresh;
                            renderDetailTab();
                        }
                    } catch(err: any) { toast('Eroare: ' + err.message, 'error'); }
                }
            });
        });
        // Add chapter manually
        const addBtn = el.querySelector('.doc-add-ch-btn');
        if (addBtn) {
            addBtn.addEventListener('click', async () => {
                const docId = el.querySelector('.doc-add-doc-select')?.value;
                const secId = el.querySelector('.doc-add-sec-select')?.value || null;
                const chNum = el.querySelector('.doc-add-ch-num')?.value?.trim();
                const chTitle = el.querySelector('.doc-add-ch-title')?.value?.trim();
                const startPage = parseInt(el.querySelector('.doc-add-ch-start')?.value) || null;
                const endPage = parseInt(el.querySelector('.doc-add-ch-end')?.value) || null;
                if (!docId || !chTitle) { toast('Selecteaza document si introdu titlu', 'error'); return; }
                try {
                    const res = await fetch(`/api/documents/${docId}/chapters`, {
                        method:'POST', credentials:'include',
                        headers:{'Content-Type':'application/json'},
                        body: JSON.stringify({
                            chapter_number: chNum,
                            title: chTitle,
                            start_page: startPage,
                            end_page: endPage,
                            template_section_id: secId ? parseInt(secId) : null,
                            chapter_type: 'other'
                        })
                    });
                    if (res.ok) {
                        toast('Capitol adaugat');
                        if ((window as any)._detailLineId) {
                            const fresh = await api(`/api/line_detail/${(window as any)._detailLineId}`);
                            (window as any)._detailData = fresh;
                            renderDetailTab();
                        }
                    } else {
                        const data = await res.json();
                        toast(data.error || 'Eroare', 'error');
                    }
                } catch(err: any) { toast('Eroare: ' + err.message, 'error'); }
            });
        }
        // Tree: allocate document to section
        el.querySelectorAll('.doc-tree-alloc').forEach((sel: Element) => {
            const selEl = sel as HTMLSelectElement;
            selEl.addEventListener('change', async (e: Event) => {
                e.stopPropagation();
                const docId = selEl.value;
                const sectionId = selEl.dataset.sectionId;
                if (!docId) return;
                try {
                    const res = await fetch(`/api/documents/${docId}/chapters`, {
                        method:'POST', credentials:'include',
                        headers:{'Content-Type':'application/json'},
                        body: JSON.stringify({
                            template_section_id: parseInt(String(sectionId || '0'), 10),
                            title: selEl.options[selEl.selectedIndex].text,
                            chapter_type: 'other',
                            chapter_number: '',
                            start_page: null, end_page: null
                        })
                    });
                    if (res.ok) {
                        toast('Alocat');
                        if ((window as any)._detailLineId) {
                            const fresh = await api(`/api/line_detail/${(window as any)._detailLineId}`);
                            (window as any)._detailData = fresh;
                            renderDetailTab();
                        }
                    } else {
                        const data = await res.json();
                        toast(data.error || 'Eroare', 'error');
                    }
                } catch(err: any) { toast('Eroare: ' + err.message, 'error'); }
            });
        });
        // SACMI categories: open document
        el.querySelectorAll('.doc-cat-open').forEach((span: Element) => {
            span.addEventListener('click', () => {
                const fn = (span as HTMLElement).dataset.filename;
                if (fn) window.open(`/uploads/${fn}`, '_blank');
            });
        });
        // SACMI categories: remove document (Master, with confirmation)
        el.querySelectorAll('.doc-cat-remove').forEach((btn: Element) => {
            btn.addEventListener('click', async (e: Event) => {
                e.stopPropagation();
                const hb = btn as HTMLElement;
                const docDesc = hb.dataset.desc || 'acest document';
                if (!await askConfirm(`Stergi documentul "${docDesc}" din aceasta categorie?`, `Documentul ramane in baza de date dar nu mai este alocat.`)) return;
                const mdId = hb.dataset.mdId;
                try {
                    await fetch(`/api/machine-documents/${mdId}`, {method:'DELETE', credentials:'include'});
                    toast('Document sters din categorie');
                    if ((window as any)._detailLineId) {
                        const fresh = await api(`/api/line_detail/${(window as any)._detailLineId}`);
                        (window as any)._detailData = fresh;
                        renderDetailTab();
                    }
                } catch(err: any) { toast('Eroare: ' + err.message, 'error'); }
            });
        });
        // Chapter toggle (expand/collapse)
        el.querySelectorAll('.doc-cat-ch-toggle').forEach((span: Element) => {
            span.addEventListener('click', (e: Event) => {
                e.stopPropagation();
                const targetId = (span as HTMLElement).dataset.toggle;
                const target = document.getElementById(String(targetId || ''));
                if (target) {
                    target.classList.toggle('hidden');
                    span.classList.toggle('expanded');
                }
            });
        });
        // Chapter: open PDF at page (both number and title are clickable)
        el.querySelectorAll('.doc-ch-num-link, .doc-ch-title-link').forEach((span: Element) => {
            span.addEventListener('click', (e: Event) => {
                e.stopPropagation();
                const hs = span as HTMLElement;
                const docId = hs.dataset.docId;
                const page = parseInt(hs.dataset.page || '1', 10) || 1;
                const doc = ((window as any)._detailData?.documents || []).find((docRow: any) => docRow.id == docId);
                if (doc && doc.filename) {
                    window.open(`/pdf-viewer?file=${encodeURIComponent(doc.filename)}&page=${page}`, '_blank');
                }
            });
        });
        // Chapter: delete (Master)
        el.querySelectorAll('.doc-ch-del').forEach((btn: Element) => {
            btn.addEventListener('click', async (e: Event) => {
                e.stopPropagation();
                if (!await askConfirm('Stergi acest capitol?')) return;
                const hb = btn as HTMLElement;
                const chId = hb.dataset.chId;
                const docId = hb.dataset.docId;
                try {
                    await fetch(`/api/documents/${docId}/chapters/${chId}`, {method:'DELETE', credentials:'include'});
                    toast('Capitol sters');
                    if ((window as any)._detailLineId) {
                        const fresh = await api(`/api/line_detail/${(window as any)._detailLineId}`);
                        (window as any)._detailData = fresh;
                        renderDetailTab();
                    }
                } catch(err: any) { toast('Eroare: ' + err.message, 'error'); }
            });
        });
        // Chapter: auto-detect from categories view (Master)
        el.querySelectorAll('.doc-cat-detect-ch').forEach((btn: Element) => {
            const b = btn as HTMLButtonElement;
            b.addEventListener('click', async (e: Event) => {
                e.stopPropagation();
                const docId = b.dataset.docId;
                b.disabled = true;
                b.textContent = '...';
                try {
                    const res = await fetch(`/api/documents/${docId}/auto-detect-chapters`, {method:'POST', credentials:'include'});
                    const data = await res.json();
                    if (res.ok) {
                        toast(`${data.detected} capitole detectate`, 'success');
                        if ((window as any)._detailLineId) {
                            const fresh = await api(`/api/line_detail/${(window as any)._detailLineId}`);
                            (window as any)._detailData = fresh;
                            renderDetailTab();
                        }
                    } else { toast(data.error || 'Eroare', 'error'); }
                } catch(err: any) { toast('Eroare: ' + err.message, 'error'); }
                b.disabled = false;
                b.textContent = 'Capitole';
            });
        });
        // Chapter: add manually (Master)
        el.querySelectorAll('.doc-ch-add-btn').forEach((btn: HTMLElement) => {
            btn.addEventListener('click', async () => {
                const row = btn.closest('.doc-ch-add-row') as HTMLElement;
                const docId = btn.dataset.docId;
                const num = (row.querySelector('.doc-ch-add-num') as HTMLInputElement).value.trim();
                const title = (row.querySelector('.doc-ch-add-title') as HTMLInputElement).value.trim();
                const startP = (row.querySelector('.doc-ch-add-start') as HTMLInputElement).value;
                const endP = (row.querySelector('.doc-ch-add-end') as HTMLInputElement).value;
                if (!title) { toast('Introduceti titlul', 'error'); return; }
                try {
                    const res = await fetch(`/api/documents/${docId}/chapters`, {
                        method:'POST', credentials:'include',
                        headers:{'Content-Type':'application/json'},
                        body: JSON.stringify({
                            chapter_number: num, title: title,
                            start_page: startP ? parseInt(startP) : null,
                            end_page: endP ? parseInt(endP) : null,
                            chapter_type: 'other'
                        })
                    });
                    if (res.ok) {
                        toast('Capitol adaugat', 'success');
                        if ((window as any)._detailLineId) {
                            const fresh = await api(`/api/line_detail/${(window as any)._detailLineId}`);
                            (window as any)._detailData = fresh;
                            renderDetailTab();
                        }
                    } else {
                        const data = await res.json();
                        toast(data.error || 'Eroare', 'error');
                    }
                } catch(err: any) { toast('Eroare: ' + err.message, 'error'); }
            });
        });
        // SACMI categories: quick upload to category
        el.querySelectorAll('.doc-cat-upload-btn').forEach((btn: HTMLElement) => {
            btn.addEventListener('click', () => {
                const secId = btn.dataset.sectionId;
                const secSelect = document.querySelector('#doc-upload-form select[name="template_section_id"]') as HTMLSelectElement | null;
                if (secSelect) {
                    secSelect.value = secId || '';
                    secSelect.scrollIntoView({behavior:'smooth', block:'center'});
                    secSelect.style.outline = '2px solid #10b981';
                    setTimeout(() => { secSelect.style.outline = ''; }, 2000);
                }
                const fileInput = document.querySelector('#doc-upload-form input[type="file"]');
                if (fileInput) fileInput.scrollIntoView({behavior:'smooth', block:'center'});
            });
        });
        refreshDocumentProcessingStatus();
        const analyzeAllBtn = document.getElementById('btn-analyze-all-imgs') as HTMLButtonElement | null;
        if (analyzeAllBtn) {
            analyzeAllBtn.addEventListener('click', async () => {
                if (!await askConfirm('Analizează cu AI Vision TOATE imaginile din documentele procesate?', 'Acest proces poate dura câteva minute și consumă tokeni API.')) return;
                analyzeAllBtn.disabled = true;
                const statusEl = document.getElementById('analyze-all-status');
                if (statusEl) statusEl.textContent = '⏳ Se analizează... (poate dura câteva minute)';
                try {
                    const res = await fetch('/api/documents/analyze_all_images', {
                        method: 'POST', credentials: 'include',
                        headers: {'Content-Type': 'application/json'},
                        body: JSON.stringify({machine_id: machineId})
                    });
                    const data = await res.json();
                    if (res.ok) {
                        toast(`${data.total_analyzed} imagini analizate din ${(data.documents||[]).length} documente`, 'success');
                        if (statusEl) statusEl.textContent = `✅ ${data.total_analyzed} imagini analizate`;
                        if ((window as any)._detailLineId) { const fresh = await api(`/api/line_detail/${(window as any)._detailLineId}`); (window as any)._detailData = fresh; renderDetailTab(); }
                    } else {
                        toast(data.error || 'Eroare', 'error');
                        if (statusEl) statusEl.textContent = '❌ ' + (data.error || 'Eroare');
                    }
                } catch(err: any) {
                    toast('Eroare: ' + err.message, 'error');
                    if (statusEl) statusEl.textContent = '❌ Eroare conexiune';
                }
                analyzeAllBtn.disabled = false;
            });
        }
        const fixPagesBtn = document.getElementById('btn-fix-page-ranges') as HTMLButtonElement | null;
        if (fixPagesBtn) {
            fixPagesBtn.addEventListener('click', async () => {
                if (!await askConfirm('Corelează imaginile cu procedurile pentru toate documentele?', 'Acest proces identifică paginile sursă ale fiecărei proceduri.')) return;
                fixPagesBtn.disabled = true;
                const statusEl = document.getElementById('analyze-all-status');
                if (statusEl) statusEl.textContent = '⏳ Se corelează...';
                try {
                    const res = await fetch('/api/documents/fix_all_page_ranges', {
                        method: 'POST', credentials: 'include',
                        headers: {'Content-Type': 'application/json'},
                        body: '{}'
                    });
                    const data = await res.json();
                    if (res.ok) {
                        toast(`${data.total_updated} extract-uri actualizate cu pagini sursă`, 'success');
                        if (statusEl) statusEl.textContent = `✅ ${data.total_updated} corelate`;
                    } else {
                        toast(data.error || 'Eroare', 'error');
                        if (statusEl) statusEl.textContent = '❌ ' + (data.error || 'Eroare');
                    }
                } catch(err: any) {
                    toast('Eroare: ' + err.message, 'error');
                    if (statusEl) statusEl.textContent = '❌ Eroare conexiune';
                }
                fixPagesBtn.disabled = false;
            });
        }
        el.querySelectorAll('.doc-detect-chapters-btn').forEach((btn: Element) => {
            const b = btn as HTMLButtonElement;
            b.addEventListener('click', async () => {
                const docId = b.dataset.docId;
                b.disabled = true;
                b.textContent = 'Se detecteaza...';
                try {
                    const res = await fetch(`/api/documents/${docId}/auto-detect-chapters`, {method:'POST', credentials:'include'});
                    const data = await res.json();
                    if (res.ok) {
                        toast(`${data.detected} capitole detectate`, 'success');
                        if ((window as any)._detailLineId) {
                            const fresh = await api(`/api/line_detail/${(window as any)._detailLineId}`);
                            (window as any)._detailData = fresh;
                            renderDetailTab();
                        }
                    } else {
                        toast(data.error || 'Eroare', 'error');
                        b.disabled = false;
                        b.textContent = 'Capitole';
                    }
                } catch(err: any) {
                    toast('Eroare: ' + err.message, 'error');
                    b.disabled = false;
                    b.textContent = 'Capitole';
                }
            });
        });
        const form = document.getElementById('doc-upload-form') as HTMLFormElement | null;
        if (form) {
            const fileInput = form.querySelector('input[name="file"]') as HTMLInputElement | null;
            const fileCountEl = document.getElementById('doc-upload-file-count');
            if (fileInput && fileCountEl) {
                fileInput.addEventListener('change', () => {
                    const n = fileInput.files ? fileInput.files.length : 0;
                    fileCountEl.textContent = n > 1 ? `${n} fișiere selectate` : '';
                });
            }
            form.addEventListener('submit', async (e) => {
                e.preventDefault();
                const files = fileInput ? fileInput.files : null;
                if (!files || files.length === 0) { toast('Selectează cel puțin un fișier', 'error'); return; }
                const submitBtn = form.querySelector('button[type="submit"]') as HTMLButtonElement | null;
                const total = files.length;
                let ok = 0, fail = 0;
                if (submitBtn) { submitBtn.disabled = true; }
                for (let i = 0; i < total; i++) {
                    if (submitBtn) submitBtn.textContent = `Se încarcă ${i + 1}/${total}…`;
                    const fd = new FormData();
                    fd.append('machine_id', (form.querySelector('input[name="machine_id"]') as HTMLInputElement)?.value || '');
                    const langSel = form.querySelector('select[name="language"]') as HTMLSelectElement | null;
                    if (langSel) fd.append('language', langSel.value);
                    const secSel = form.querySelector('select[name="template_section_id"]') as HTMLSelectElement | null;
                    if (secSel) fd.append('template_section_id', secSel.value);
                    const typeSel = form.querySelector('select[name="doc_type"]') as HTMLSelectElement | null;
                    if (typeSel) fd.append('doc_type', typeSel.value);
                    const descInp = form.querySelector('input[name="description"]') as HTMLInputElement | null;
                    if (descInp) fd.append('description', descInp.value);
                    const codeInp = form.querySelector('input[name="doc_code"]') as HTMLInputElement | null;
                    if (codeInp) fd.append('doc_code', codeInp.value);
                    fd.append('file', files[i]);
                    try {
                        const res = await fetch('/api/documents', { method: 'POST', body: fd });
                        const data = await res.json();
                        if (res.ok) ok++; else { fail++; toast(`Eroare ${files[i].name}: ${data.error || 'necunoscută'}`, 'error'); }
                    } catch (err: any) { fail++; toast(`Eroare ${files[i].name}: ${err.message}`, 'error'); }
                }
                toast(`Încărcat: ${ok} document${ok !== 1 ? 'e' : ''}${fail ? `, ${fail} erori` : ''}`, fail ? 'warning' : 'success');
                if ((window as any)._detailLineId) {
                    const fresh = await api(`/api/line_detail/${(window as any)._detailLineId}`);
                    (window as any)._detailData = fresh;
                    renderDetailTab();
                }
                if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = 'Încarcă'; }
            });
        }
    } else if (detailTabActive === 'extracts') {
        const machineId = String((window as any)._detailLineId || d.machine?.id || '');
        if (!d.document_extracts?.length && (d.document_extracts_count || 0) > 0) {
            el.innerHTML = '<div class="detail-loading"><div class="spinner"></div>Se încarcă extrase...</div>';
            try {
                const resp = await api(`/api/machines/${encodeURIComponent(machineId)}/document_extracts`) as { extracts?: any[] };
                d.document_extracts = resp.extracts || [];
                (window as any)._detailData = d;
            } catch (e: any) {
                el.innerHTML = `<div class="alarm-empty"><span class="icon">⚠</span>Eroare la încărcarea extraseelor: ${escHtml(e?.message || 'necunoscută')}</div>`;
                return;
            }
            return renderDetailTab();
        }
        const ex = (d.document_extracts || []).slice()
            .filter((x: any) => {
                const ch = (x.chapter_type || '').toLowerCase();
                return ch === 'adjustment' || ch === 'operation' || !ch || ch === 'other';
            });
        const byType: Record<string, any[]> = { procedure: [], reglaj: [], instructiune: [], scule: [], image_analysis: [] };
        ex.forEach((x: any) => { const t = (x.extract_type || '').toLowerCase(); if (byType[t]) byType[t].push(x); });
        const typeLabels: Record<string, string> = { procedure: 'Proceduri de lucru', reglaj: 'Reglaje & Calibrări', instructiune: 'Instrucțiuni de operare', scule: 'Scule și instrumente', image_analysis: '🖼️ Scheme și desene din imagini' };
        let html = '<p class="doc-process-hint">Proceduri de reglaj, calibrări, ajustări și instrucțiuni de operare extrase din documente.</p>';
        (['procedure', 'reglaj', 'instructiune', 'scule'] as const).forEach((tip: string) => {
            const list = byType[tip] || [];
            if (list.length === 0) return;
            html += `<h4 class="extract-type-title">${typeLabels[tip]}</h4><div class="extract-list">`;
            list.forEach((x: any, idx: number) => {
                const titlu = escHtml((x.title_ro || '').trim() || '—');
                const fullContent = (x.content_ro || '').trim();
                const preview = escHtml(truncate(fullContent, 200));
                const full = escHtml(fullContent);
                const hasMore = fullContent.length > 200;
                const eid = `extract-${tip}-${idx}`;
                let infoRows = '';
                if (x.tools) infoRows += `<div class="extract-info-row"><span class="extract-label extract-label-tools">SCULE</span><span class="extract-value">${escHtml(x.tools)}</span></div>`;
                if (x.safety_warnings) infoRows += `<div class="extract-info-row"><span class="extract-label extract-label-safety">⚠ SIGURANȚĂ</span><span class="extract-value extract-val-safety">${escHtml(x.safety_warnings)}</span></div>`;
                if (x.reference_values) infoRows += `<div class="extract-info-row"><span class="extract-label extract-label-ref">REFERINȚĂ</span><span class="extract-value">${escHtml(x.reference_values)}</span></div>`;
                if (x.video_ref) infoRows += `<div class="extract-info-row"><span class="extract-label extract-label-video">VIDEO</span><span class="extract-value">${escHtml(x.video_ref)}</span></div>`;
                const isProc = x.extract_type === 'procedure' || x.extract_type === 'instructiune';
                const dbId = x.id;
                html += `<div class="extract-item extract-clickable" data-extract-id="${eid}">
                    <strong class="extract-title-row"><span>${titlu}</span>${hasMore ? '<span class="extract-expand-icon">▶</span>' : ''}${isProc && dbId ? `<button class="proc-open-btn" data-db-id="${dbId}" title="Deschide procedura pas cu pas">▷ Procedură</button>` : ''}</strong>
                    <p class="extract-content extract-preview" id="${eid}-preview">${preview}</p>
                    <div class="extract-full hidden" id="${eid}-full"><p class="extract-content">${full}</p>${infoRows}</div>
                    ${!hasMore && infoRows ? infoRows : ''}
                </div>`;
            });
            html += '</div>';
        });
        if (ex.length === 0) {
            const docs = (d.documents || []).filter((dc: any) => dc.processed);
            if (docs.length > 0) {
                html += '<div class="alarm-empty"><p><strong>Nu apare încă conținut extras</strong> (proceduri, reglaje, instrucțiuni). Documentele sunt marcate procesate, dar extragerea acestor tipuri a fost făcută ulterior sau AI-ul nu a returnat conținut.</p><p>Apasă <strong>Reprocesează</strong> pe documentele din tab-ul <strong>Documente</strong> (ex. carte tehnică, manual) — se reîncearcă doar extragerea procedurilor și instrucțiunilor, fără să duplici intervale/piese.</p></div>';
            } else {
                html += '<div class="alarm-empty">Niciun conținut extras. Procesează documente PDF din tab-ul Documente.</div>';
            }
        }
        el.innerHTML = html;
        el.querySelectorAll('.extract-clickable').forEach((card: Element) => {
            card.addEventListener('click', (e: Event) => {
                if ((e.target as HTMLElement).closest('.proc-open-btn')) return;
                const eid = (card as HTMLElement).dataset.extractId;
                const preview = document.getElementById(eid + '-preview');
                const full = document.getElementById(eid + '-full');
                const icon = card.querySelector('.extract-expand-icon');
                if (!full) return;
                const isOpen = !full.classList.contains('hidden');
                full.classList.toggle('hidden', isOpen);
                if (preview) preview.classList.toggle('hidden', !isOpen);
                if (icon) icon.textContent = isOpen ? '▶' : '▼';
                card.classList.toggle('extract-expanded', !isOpen);
            });
        });
        el.querySelectorAll('.proc-open-btn').forEach((btn: Element) => {
            btn.addEventListener('click', (e: Event) => {
                e.stopPropagation();
                openProcedureOverlay(String((btn as HTMLElement).dataset.dbId || ''));
            });
        });
    } else if (detailTabActive === 'consum') {
        _renderEnergyMonitor(el, d);
    } else if (detailTabActive === 'conectare') {
        const mid = String((window as any)._detailLineId || d.machine?.id || '');
        const m = d.machine || {};
        const canEdit = state.currentUser && state.currentUser.role === 'master';
        const cproto = (m.connection_protocol || '').trim();
        const ci = (m.connection_ip || '').trim();
        const cp = (m.connection_port != null && String(m.connection_port).trim()) ? String(m.connection_port).trim() : '';
        const cSlaveId = m.connection_slave_id || 1;
        let cExtra: Record<string, any> = {};
        try { cExtra = JSON.parse(m.connection_extra || '{}'); } catch(e: any) { cExtra = {}; }
        const mproto = (m.meter_connection_protocol || '').trim();
        const mci = (m.meter_connection_ip || '').trim();
        const mcp = (m.meter_connection_port != null && String(m.meter_connection_port).trim()) ? String(m.meter_connection_port).trim() : '502';
        const mdevType = (m.meter_device_type || '').trim();

        const PROTOS = [
            {v:'', l:'— Selecteaza protocol'},
            {v:'modbus_tcp', l:'Modbus TCP'},
            {v:'modbus_rtu', l:'Modbus RTU (serial)'},
            {v:'opc_ua', l:'OPC UA'},
            {v:'s7', l:'S7 PUT/GET (Siemens)'},
            {v:'mqtt', l:'MQTT'},
            {v:'tcp_foldare', l:PROTO_LBL['tcp_foldare']},
            {v:'tcp_robot', l:PROTO_LBL['tcp_robot']},
            {v:'zebra_http', l:PROTO_LBL['zebra_http']},
        ];
        const protoOpts = (sel: string) => PROTOS.map(o => `<option value="${o.v}" ${o.v===sel?'selected':''}>${escHtml(o.l)}</option>`).join('');

        const _extraFields = (proto: string, extra: Record<string, any>, prefix: string) => {
            if (proto === 'modbus_tcp' || proto === 'modbus_rtu') {
                const slaveId = extra.slave_id || cSlaveId || 1;
                let h = `<div class="connection-field"><label>Slave ID</label><input type="number" id="${prefix}-slave-id" value="${slaveId}" min="1" max="247" ${canEdit?'':'disabled'}></div>`;
                if (proto === 'modbus_rtu') {
                    h += `<div class="connection-field"><label>Baudrate</label><select id="${prefix}-baudrate" ${canEdit?'':'disabled'}>`;
                    for (const br of [9600,19200,38400,57600,115200]) h += `<option value="${br}" ${(extra.baudrate||9600)==br?'selected':''}>${br}</option>`;
                    h += `</select></div>`;
                    h += `<div class="connection-field"><label>Paritate</label><select id="${prefix}-parity" ${canEdit?'':'disabled'}>
                        <option value="N" ${(extra.parity||'N')==='N'?'selected':''}>None</option>
                        <option value="E" ${extra.parity==='E'?'selected':''}>Even</option>
                        <option value="O" ${extra.parity==='O'?'selected':''}>Odd</option></select></div>`;
                }
                return h;
            }
            if (proto === 's7') {
                return `<div class="connection-field"><label>Rack</label><input type="number" id="${prefix}-rack" value="${extra.rack||0}" min="0" max="7" ${canEdit?'':'disabled'}></div>
                <div class="connection-field"><label>Slot</label><input type="number" id="${prefix}-slot" value="${extra.slot!=null?extra.slot:1}" min="0" max="31" ${canEdit?'':'disabled'}></div>
                <div class="connection-field"><label>Tip PLC</label><select id="${prefix}-plc-type" ${canEdit?'':'disabled'}>
                    <option value="S7-1200" ${(extra.plc_type||'')==='S7-1200'?'selected':''}>S7-1200 (slot=1)</option>
                    <option value="S7-1500" ${(extra.plc_type||'')==='S7-1500'?'selected':''}>S7-1500 (slot=0)</option></select></div>`;
            }
            if (proto === 'opc_ua') {
                return `<div class="connection-field"><label>Username</label><input type="text" id="${prefix}-username" value="${escHtml(extra.username||'')}" placeholder="(optional)" ${canEdit?'':'disabled'}></div>
                <div class="connection-field"><label>Password</label><input type="password" id="${prefix}-password" value="${escHtml(extra.password||'')}" placeholder="(optional)" ${canEdit?'':'disabled'}></div>`;
            }
            if (proto === 'mqtt') {
                return `<div class="connection-field"><label>Client ID</label><input type="text" id="${prefix}-client-id" value="${escHtml(extra.client_id||'')}" placeholder="fabrica_${mid||'x'}" ${canEdit?'':'disabled'}></div>
                <div class="connection-field"><label>QoS</label><select id="${prefix}-qos" ${canEdit?'':'disabled'}>
                    <option value="0" ${(extra.qos||0)==0?'selected':''}>0 - At most once</option>
                    <option value="1" ${extra.qos==1?'selected':''}>1 - At least once</option>
                    <option value="2" ${extra.qos==2?'selected':''}>2 - Exactly once</option></select></div>
                <div class="connection-field"><label>Username</label><input type="text" id="${prefix}-username" value="${escHtml(extra.username||'')}" placeholder="(optional)" ${canEdit?'':'disabled'}></div>
                <div class="connection-field"><label>Password</label><input type="password" id="${prefix}-password" value="${escHtml(extra.password||'')}" placeholder="(optional)" ${canEdit?'':'disabled'}></div>`;
            }
            return '';
        };

        const _portPlaceholder = (proto: string) => {
            if (proto === 'modbus_tcp') return '502';
            if (proto === 'modbus_rtu') return '/dev/ttyUSB0';
            if (proto === 'opc_ua') return '4840';
            if (proto === 's7') return '102';
            if (proto === 'mqtt') return '1883';
            if (proto === 'tcp_foldare' || proto === 'tcp_robot') return '4002';
            if (proto === 'zebra_http') return '80';
            return '502';
        };
        const _ipLabel = (proto: string) => {
            if (proto === 'modbus_rtu') return 'Port serial';
            if (proto === 'opc_ua') return 'Endpoint / IP';
            if (proto === 'mqtt') return 'Broker IP';
            return 'Adresa IP';
        };

        el.innerHTML = `
        <div class="connection-form-panel connection-panel-utilaj">
            <h4 class="connection-panel-title">Conexiune utilaj (PLC / dispozitiv)</h4>
            <p class="connection-desc">Selecteaza protocolul, configureaza adresa si parametrii specifici. Apoi adauga registrele/variabilele de citit.</p>
            <div class="connection-form-grid" id="conn-plc-grid">
                <div class="connection-field">
                    <label>Protocol</label>
                    <select id="detail-conn-protocol" ${canEdit?'':'disabled'}>${protoOpts(cproto)}</select>
                </div>
                <div class="connection-field">
                    <label id="lbl-conn-ip">${_ipLabel(cproto)}</label>
                    <input type="text" id="detail-conn-ip" value="${escHtml(ci)}" placeholder="${_portPlaceholder(cproto)==='/dev/ttyUSB0'?'/dev/ttyUSB0 sau COM1':'ex: 192.168.1.10'}" maxlength="120" ${canEdit?'':'disabled'}>
                </div>
                <div class="connection-field connection-field-port">
                    <label>Port</label>
                    <input type="text" id="detail-conn-port" value="${escHtml(cp)}" placeholder="${_portPlaceholder(cproto)}" maxlength="10" ${canEdit?'':'disabled'}>
                </div>
                ${_extraFields(cproto, cExtra, 'conn')}
            </div>
            ${canEdit?`<div class="connection-form-actions">
                <button class="btn btn-sm btn-green" id="detail-conn-save">Salveaza</button>
                <button class="btn btn-sm btn-blue" id="detail-conn-test">Test server</button>
                <button class="btn btn-sm btn-blue" id="detail-conn-test-collector" style="background:#7c3aed">Test colector</button>
                <button class="btn btn-sm btn-outline btn-red" id="detail-conn-delete">Sterge</button>
            </div>`:'<p class="connection-readonly">Doar master poate modifica.</p>'}
            <div id="conn-test-result" style="margin-top:8px"></div>
        </div>

        <div class="connection-form-panel connection-panel-meter">
            <h4 class="connection-panel-title">Contor curent (energie)</h4>
            <p class="connection-desc">Conexiune separata pt contorul de energie. Tip dispozitiv determina registrele citite automat.</p>
            <div class="connection-form-grid">
                <div class="connection-field"><label>Protocol</label>
                    <select id="detail-meter-protocol" ${canEdit?'':'disabled'}>${protoOpts(mproto)}</select></div>
                <div class="connection-field"><label>IP</label>
                    <input type="text" id="detail-meter-ip" value="${escHtml(mci)}" placeholder="192.168.33.98" maxlength="45" ${canEdit?'':'disabled'}></div>
                <div class="connection-field connection-field-port"><label>Port</label>
                    <input type="text" id="detail-meter-port" value="${escHtml(mcp)}" placeholder="502" maxlength="10" ${canEdit?'':'disabled'}></div>
                <div class="connection-field"><label>Tip dispozitiv</label>
                    <select id="detail-meter-device-type" ${canEdit?'':'disabled'}>
                        <option value="PAC3220" ${mdevType==='PAC3220'||!mdevType?'selected':''}>Siemens PAC3220</option>
                        <option value="PAC3120" ${mdevType==='PAC3120'?'selected':''}>Siemens PAC3120</option>
                        <option value="generic" ${mdevType==='generic'?'selected':''}>Generic</option></select></div>
            </div>
            ${canEdit?`<div class="connection-form-actions">
                <button class="btn btn-sm btn-green" id="detail-meter-save">Salveaza</button>
                <button class="btn btn-sm btn-blue" id="detail-meter-test">Test server</button>
                <button class="btn btn-sm btn-blue" id="detail-meter-test-collector" style="background:#7c3aed">Test colector</button>
                <button class="btn btn-sm btn-outline btn-red" id="detail-meter-delete">Sterge</button>
            </div>`:''}
            <div id="meter-test-result" style="margin-top:8px"></div>
        </div>

        <div class="connection-form-panel" id="conn-registers-panel">
            <h4 class="connection-panel-title">Registre / Variabile de citit</h4>
            <p class="connection-desc">Defineste ce date citeste colectorul de la acest utilaj. Poti importa dintr-un profil predefinit sau adauga manual.</p>
            ${canEdit?`<div class="connection-form-actions" style="margin-bottom:10px">
                <select id="conn-profile-select" style="max-width:220px"><option value="">— Import profil —</option></select>
                <button class="btn btn-sm btn-blue" id="conn-profile-import">Importa registre din profil</button>
                <button class="btn btn-sm btn-green" id="conn-reg-add">+ Adauga registru</button>
                <button class="btn btn-sm btn-green" id="conn-reg-save-all">Salveaza registre</button>
            </div>`:''}
            <div id="conn-registers-table" style="overflow-x:auto"></div>
        </div>

        <div class="connection-form-panel" style="margin-top:12px">
            <h4 class="connection-panel-title">Jurnal conexiuni</h4>
            <div id="conn-log-list" style="max-height:200px;overflow-y:auto;font-size:0.85em"></div>
        </div>`;

        // ── Dynamic protocol fields update ──
        const protoSelect = document.getElementById('detail-conn-protocol') as HTMLSelectElement | null;
        if (protoSelect) {
            protoSelect.addEventListener('change', () => {
                const p = protoSelect.value;
                const grid = document.getElementById('conn-plc-grid');
                const lblIp = document.getElementById('lbl-conn-ip');
                const ipEl = document.getElementById('detail-conn-ip') as HTMLInputElement | null;
                const portEl = document.getElementById('detail-conn-port') as HTMLInputElement | null;
                if (!grid) return;
                if (lblIp) lblIp.textContent = _ipLabel(p);
                if (ipEl) ipEl.placeholder = _portPlaceholder(p)==='/dev/ttyUSB0'?'/dev/ttyUSB0 sau COM1':'ex: 192.168.1.10';
                if (portEl) portEl.placeholder = _portPlaceholder(p);
                // Remove old extra fields and re-add
                grid.querySelectorAll('.conn-extra-field').forEach((el: Element) => el.remove());
                const extraHtml = _extraFields(p, {}, 'conn');
                if (extraHtml) {
                    const tmp = document.createElement('div');
                    tmp.innerHTML = extraHtml;
                    Array.from(tmp.children).forEach((ch: Element) => { ch.classList.add('conn-extra-field'); grid.appendChild(ch); });
                }
            });
        }

        // ── Save PLC connection ──
        const saveBtn = document.getElementById('detail-conn-save') as HTMLButtonElement | null;
        if (saveBtn && mid && canEdit) {
            saveBtn.addEventListener('click', async () => {
                const proto = (document.getElementById('detail-conn-protocol') as HTMLSelectElement | null)?.value || '';
                const ip = (document.getElementById('detail-conn-ip') as HTMLInputElement | null)?.value?.trim() || '';
                const port = (document.getElementById('detail-conn-port') as HTMLInputElement | null)?.value?.trim() || '';
                if (!proto && !ip) { toast('Selecteaza protocol si IP.','error'); return; }
                const extra: Record<string, any> = {};
                const slaveEl = document.getElementById('conn-slave-id') as HTMLInputElement | null;
                if (slaveEl) extra.slave_id = parseInt(slaveEl.value, 10)||1;
                const rackEl = document.getElementById('conn-rack') as HTMLInputElement | null;
                if (rackEl) extra.rack = parseInt(rackEl.value, 10)||0;
                const slotEl = document.getElementById('conn-slot') as HTMLInputElement | null;
                if (slotEl) extra.slot = parseInt(slotEl.value, 10)||1;
                const plcTypeEl = document.getElementById('conn-plc-type') as HTMLSelectElement | null;
                if (plcTypeEl) extra.plc_type = plcTypeEl.value;
                for (const k of ['baudrate','parity','username','password','client-id','qos']) {
                    const fel = document.getElementById('conn-'+k) as HTMLInputElement | HTMLSelectElement | null;
                    if (fel) extra[k.replace('-','_')] = (fel as HTMLInputElement).value;
                }
                const payload = {
                    connection_protocol: proto, connection_ip: ip, connection_port: port,
                    connection_type: proto ? 'ethernet' : '',
                    connection_slave_id: extra.slave_id || 1,
                    connection_extra: JSON.stringify(extra),
                };
                saveBtn.disabled = true; saveBtn.textContent = 'Se salveaza...';
                try {
                    const res = await fetch(`/api/config/machines/${encodeURIComponent(mid)}`,
                        {method:'PUT',headers:{'Content-Type':'application/json'},body:JSON.stringify(payload),credentials:'include'});
                    if (!res.ok) { const e = await res.json().catch(()=>({})); toast(e.error||'Eroare','error'); return; }
                    toast('Conexiune utilaj salvata.');
                    const fresh = await api(`/api/line_detail/${mid}`); (window as any)._detailData = fresh; renderDetailTab();
                } catch(e: any) { toast('Eroare: '+e.message,'error'); }
                finally { saveBtn.disabled=false; saveBtn.textContent='Salveaza'; }
            });
        }

        // ── Test PLC connection ──
        const testBtn = document.getElementById('detail-conn-test') as HTMLButtonElement | null;
        if (testBtn && canEdit) {
            testBtn.addEventListener('click', async () => {
                const proto = (document.getElementById('detail-conn-protocol') as HTMLSelectElement | null)?.value || '';
                const ip = (document.getElementById('detail-conn-ip') as HTMLInputElement | null)?.value?.trim() || '';
                const port = (document.getElementById('detail-conn-port') as HTMLInputElement | null)?.value?.trim() || '';
                if (!proto || !ip) { toast('Selecteaza protocol si IP.','error'); return; }
                const resultDiv = document.getElementById('conn-test-result');
                testBtn.disabled = true; testBtn.textContent = 'Se testeaza...';
                if (resultDiv) resultDiv.innerHTML = '<span style="color:var(--clr-warn)">Se testeaza conexiunea de pe server...</span>';
                const body: Record<string, any> = {protocol:proto, ip:ip, port:port, machine_id:mid};
                const slaveEl = document.getElementById('conn-slave-id') as HTMLInputElement | null;
                if (slaveEl) body.slave_id = parseInt(slaveEl.value, 10)||1;
                const rackEl = document.getElementById('conn-rack') as HTMLInputElement | null;
                if (rackEl) body.rack = parseInt(rackEl.value, 10)||0;
                const slotEl = document.getElementById('conn-slot') as HTMLInputElement | null;
                if (slotEl) body.slot = parseInt(slotEl.value, 10)||1;
                const plcTypeEl = document.getElementById('conn-plc-type') as HTMLSelectElement | null;
                if (plcTypeEl) body.plc_type = plcTypeEl.value;
                for (const k of ['username','password','client_id','qos']) {
                    const fel = document.getElementById('conn-'+k.replace('_','-')) as HTMLInputElement | null;
                    if (fel) body[k] = fel.value;
                }
                try {
                    const res = await fetch('/api/connection/test',
                        {method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(body),credentials:'include'});
                    const data = await res.json();
                    if (resultDiv) {
                        const color = data.ok ? 'var(--clr-ok,#2a2)' : 'var(--clr-crit,#c22)';
                        const icon = data.ok ? '&#10004;' : '&#10008;';
                        let extra = '';
                        if (data.details?.latency_ms) extra += ` (${data.details.latency_ms}ms)`;
                        if (data.details?.cpu_info?.module_type) extra += ` - ${data.details.cpu_info.module_type}`;
                        let warning = '';
                        if (!data.ok && /192\.168\.|10\.|172\.(1[6-9]|2\d|3[01])\./.test(ip)) {
                            warning = '<br><span style="color:var(--clr-warn);font-size:.85rem">⚠ IP-ul e din rețeaua locală a fabricii. Testul de pe server (Hetzner) nu poate ajunge la acest IP. Conexiunea funcționează doar prin <b>Colectorul v2</b> instalat local.</span>';
                        }
                        resultDiv.innerHTML = `<span style="color:${color};font-weight:600">${icon} ${escHtml(data.message||'')}${extra}</span>${warning}`;
                    }
                } catch(e: any) { if (resultDiv) resultDiv.innerHTML = `<span style="color:red">Eroare: ${escHtml(e.message)}</span>`; }
                finally { testBtn.disabled=false; testBtn.textContent='Test server'; }
            });
        }

        // ── Test PLC via collector ──
        const testCollBtn = document.getElementById('detail-conn-test-collector') as HTMLButtonElement | null;
        if (testCollBtn && canEdit) {
            testCollBtn.addEventListener('click', async () => {
                const proto = (document.getElementById('detail-conn-protocol') as HTMLSelectElement | null)?.value || '';
                const ip = (document.getElementById('detail-conn-ip') as HTMLInputElement | null)?.value?.trim() || '';
                const port = (document.getElementById('detail-conn-port') as HTMLInputElement | null)?.value?.trim() || '';
                if (!proto || !ip) { toast('Selecteaza protocol si IP.','error'); return; }
                const resultDiv = document.getElementById('conn-test-result');
                testCollBtn.disabled = true; testCollBtn.textContent = 'Se trimite...';
                if (resultDiv) resultDiv.innerHTML = '<span style="color:#7c3aed">Se trimite cererea la colector...</span>';
                const body: Record<string, any> = {protocol:proto, ip:ip, port:port, machine_id:mid};
                const slaveEl = document.getElementById('conn-slave-id') as HTMLInputElement | null;
                if (slaveEl) body.slave_id = parseInt(slaveEl.value, 10)||1;
                for (const k of ['username','password']) {
                    const fel = document.getElementById('conn-'+k) as HTMLInputElement | null;
                    if (fel) body[k] = fel.value;
                }
                try {
                    const res = await fetch('/api/collector/test-request',
                        {method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(body),credentials:'include'});
                    const data = await res.json();
                    if (!data.test_id) { if (resultDiv) resultDiv.innerHTML = '<span style="color:red">Eroare: nu s-a creat cererea</span>'; return; }
                    if (resultDiv) resultDiv.innerHTML = '<span style="color:#7c3aed">⏳ Astept raspuns de la colector (max 30s)...</span>';
                    const testId = data.test_id;
                    for (let i = 0; i < 15; i++) {
                        await new Promise(r => setTimeout(r, 2000));
                        const r2 = await fetch('/api/collector/test-result/' + testId, {credentials:'include'});
                        const d2 = await r2.json();
                        if (d2.status === 'completed') {
                            const color = d2.ok ? 'var(--clr-ok,#2a2)' : 'var(--clr-crit,#c22)';
                            const icon = d2.ok ? '&#10004;' : '&#10008;';
                            if (resultDiv) resultDiv.innerHTML = `<span style="color:${color};font-weight:600">${icon} [Colector] ${escHtml(d2.message||'')}</span>`;
                            return;
                        }
                    }
                    if (resultDiv) resultDiv.innerHTML = '<span style="color:var(--clr-warn)">Timeout - colectorul nu a raspuns. Verificati daca ruleaza.</span>';
                } catch(e: any) { if (resultDiv) resultDiv.innerHTML = `<span style="color:red">Eroare: ${escHtml(e.message)}</span>`; }
                finally { testCollBtn.disabled=false; testCollBtn.textContent='Test colector'; }
            });
        }

        // ── Delete PLC connection ──
        const deleteBtn = document.getElementById('detail-conn-delete') as HTMLButtonElement | null;
        if (deleteBtn && mid && canEdit) {
            deleteBtn.addEventListener('click', async () => {
                if (!await askConfirm('Stergi conexiunea utilajului?')) return;
                deleteBtn.disabled = true;
                try {
                    await fetch(`/api/config/machines/${encodeURIComponent(mid)}`,
                        {method:'PUT',headers:{'Content-Type':'application/json'},
                         body:JSON.stringify({connection_type:'',connection_ip:'',connection_port:'',connection_protocol:'',connection_extra:'{}'}),credentials:'include'});
                    toast('Conexiune stearsa.'); const fresh = await api(`/api/line_detail/${mid}`); (window as any)._detailData = fresh; renderDetailTab();
                } catch(e: any) { toast('Eroare: '+e.message,'error'); }
                finally { deleteBtn.disabled=false; }
            });
        }

        // ── Save meter ──
        const meterSaveBtn = document.getElementById('detail-meter-save') as HTMLButtonElement | null;
        if (meterSaveBtn && mid && canEdit) {
            meterSaveBtn.addEventListener('click', async () => {
                const proto = (document.getElementById('detail-meter-protocol') as HTMLSelectElement | null)?.value||'';
                const ip = (document.getElementById('detail-meter-ip') as HTMLInputElement | null)?.value?.trim()||'';
                const port = (document.getElementById('detail-meter-port') as HTMLInputElement | null)?.value?.trim()||'502';
                const devType = (document.getElementById('detail-meter-device-type') as HTMLSelectElement | null)?.value||'PAC3220';
                const payload = {meter_connection_protocol:proto, meter_connection_ip:ip, meter_connection_port:port, meter_device_type:devType};
                meterSaveBtn.disabled=true; meterSaveBtn.textContent='Se salveaza...';
                try {
                    const res = await fetch(`/api/config/machines/${encodeURIComponent(mid)}`,
                        {method:'PUT',headers:{'Content-Type':'application/json'},body:JSON.stringify(payload),credentials:'include'});
                    if (!res.ok) { toast('Eroare la salvare','error'); return; }
                    toast('Contor salvat.'); const fresh = await api(`/api/line_detail/${mid}`); (window as any)._detailData = fresh; renderDetailTab();
                } catch(e: any) { toast('Eroare: '+e.message,'error'); }
                finally { meterSaveBtn.disabled=false; meterSaveBtn.textContent='Salveaza'; }
            });
        }

        // ── Test meter ──
        const meterTestBtn = document.getElementById('detail-meter-test') as HTMLButtonElement | null;
        if (meterTestBtn && canEdit) {
            meterTestBtn.addEventListener('click', async () => {
                const proto = (document.getElementById('detail-meter-protocol') as HTMLSelectElement | null)?.value||'modbus_tcp';
                const ip = (document.getElementById('detail-meter-ip') as HTMLInputElement | null)?.value?.trim()||'';
                if (!ip) { toast('IP contor nespecificat','error'); return; }
                const port = (document.getElementById('detail-meter-port') as HTMLInputElement | null)?.value?.trim()||'502';
                const resultDiv = document.getElementById('meter-test-result');
                meterTestBtn.disabled=true; meterTestBtn.textContent='Se testeaza...';
                if (resultDiv) resultDiv.innerHTML = '<span style="color:var(--clr-warn)">Se testeaza de pe server...</span>';
                try {
                    const res = await fetch('/api/connection/test',
                        {method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({protocol:proto,ip:ip,port:port,machine_id:mid}),credentials:'include'});
                    const data = await res.json();
                    if (resultDiv) {
                        const color = data.ok ? 'var(--clr-ok,#2a2)' : 'var(--clr-crit,#c22)';
                        let warning = '';
                        if (!data.ok && /192\.168\.|10\.|172\.(1[6-9]|2\d|3[01])\./.test(ip)) {
                            warning = '<br><span style="color:var(--clr-warn);font-size:.85rem">⚠ IP local → testul de pe server nu poate ajunge la el. Conexiunea funcționează doar prin <b>Colectorul v2</b> local.</span>';
                        }
                        resultDiv.innerHTML = `<span style="color:${color};font-weight:600">${data.ok?'&#10004;':'&#10008;'} ${escHtml(data.message||'')}</span>${warning}`;
                    }
                } catch(e: any) { if (resultDiv) resultDiv.innerHTML = `<span style="color:red">${escHtml(e.message)}</span>`; }
                finally { meterTestBtn.disabled=false; meterTestBtn.textContent='Test server'; }
            });
        }

        // ── Test meter via collector ──
        const meterTestCollBtn = document.getElementById('detail-meter-test-collector') as HTMLButtonElement | null;
        if (meterTestCollBtn && canEdit) {
            meterTestCollBtn.addEventListener('click', async () => {
                const proto = (document.getElementById('detail-meter-protocol') as HTMLSelectElement | null)?.value||'modbus_tcp';
                const ip = (document.getElementById('detail-meter-ip') as HTMLInputElement | null)?.value?.trim()||'';
                if (!ip) { toast('IP contor nespecificat','error'); return; }
                const port = (document.getElementById('detail-meter-port') as HTMLInputElement | null)?.value?.trim()||'502';
                const resultDiv = document.getElementById('meter-test-result');
                meterTestCollBtn.disabled=true; meterTestCollBtn.textContent='Se trimite...';
                if (resultDiv) resultDiv.innerHTML = '<span style="color:#7c3aed">Se trimite cererea la colector...</span>';
                try {
                    const res = await fetch('/api/collector/test-request',
                        {method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({protocol:proto,ip:ip,port:port,machine_id:mid}),credentials:'include'});
                    const data = await res.json();
                    if (!data.test_id) { if (resultDiv) resultDiv.innerHTML = '<span style="color:red">Eroare</span>'; return; }
                    if (resultDiv) resultDiv.innerHTML = '<span style="color:#7c3aed">⏳ Astept raspuns de la colector (max 30s)...</span>';
                    for (let i = 0; i < 15; i++) {
                        await new Promise(r => setTimeout(r, 2000));
                        const r2 = await fetch('/api/collector/test-result/' + data.test_id, {credentials:'include'});
                        const d2 = await r2.json();
                        if (d2.status === 'completed') {
                            const color = d2.ok ? 'var(--clr-ok,#2a2)' : 'var(--clr-crit,#c22)';
                            if (resultDiv) resultDiv.innerHTML = `<span style="color:${color};font-weight:600">${d2.ok?'&#10004;':'&#10008;'} [Colector] ${escHtml(d2.message||'')}</span>`;
                            return;
                        }
                    }
                    if (resultDiv) resultDiv.innerHTML = '<span style="color:var(--clr-warn)">Timeout - colectorul nu a raspuns.</span>';
                } catch(e: any) { if (resultDiv) resultDiv.innerHTML = `<span style="color:red">${escHtml(e.message)}</span>`; }
                finally { meterTestCollBtn.disabled=false; meterTestCollBtn.textContent='Test colector'; }
            });
        }

        // ── Delete meter ──
        const meterDeleteBtn = document.getElementById('detail-meter-delete') as HTMLButtonElement | null;
        if (meterDeleteBtn && mid && canEdit) {
            meterDeleteBtn.addEventListener('click', async () => {
                if (!await askConfirm('Stergi conexiunea contorului?')) return;
                try {
                    await fetch(`/api/config/machines/${encodeURIComponent(mid)}`,
                        {method:'PUT',headers:{'Content-Type':'application/json'},
                         body:JSON.stringify({meter_connection_ip:'',meter_connection_port:'',meter_connection_protocol:'',meter_device_type:''}),credentials:'include'});
                    toast('Contor sters.'); const fresh = await api(`/api/line_detail/${mid}`); (window as any)._detailData = fresh; renderDetailTab();
                } catch(e: any) { toast('Eroare: '+e.message,'error'); }
            });
        }

        // ── Load registers table ──
        const _renderRegsTable = (regs: any[]) => {
            const tbl = document.getElementById('conn-registers-table');
            if (!tbl) return;
            if (!regs || regs.length === 0) {
                tbl.innerHTML = '<p style="color:var(--clr-muted);padding:8px">Niciun registru configurat. Importa dintr-un profil sau adauga manual.</p>';
                return;
            }
            let h = `<table class="conn-reg-tbl"><thead><tr>
                <th>Adresa</th><th>Tip</th><th>Format</th><th>Sensor Key</th><th>Eticheta</th><th>Unitate</th><th>Scala</th>
                ${canEdit?'<th></th>':''}</tr></thead><tbody>`;
            regs.forEach((r: any, i: number) => {
                h += `<tr data-idx="${i}">
                    <td>${canEdit?`<input type="text" class="reg-addr" value="${escHtml(r.register_address||'')}" style="width:90px">`:`<code>${escHtml(r.register_address||'')}</code>`}</td>
                    <td>${canEdit?`<select class="reg-type"><option value="holding" ${r.register_type==='holding'?'selected':''}>Holding</option><option value="input" ${r.register_type==='input'?'selected':''}>Input</option><option value="db" ${r.register_type==='db'?'selected':''}>DB (S7)</option><option value="node" ${r.register_type==='node'?'selected':''}>Node (OPC)</option><option value="topic" ${r.register_type==='topic'?'selected':''}>Topic (MQTT)</option></select>`:escHtml(r.register_type||'')}</td>
                    <td>${canEdit?`<select class="reg-fmt"><option value="float32" ${r.data_format==='float32'?'selected':''}>float32</option><option value="float64" ${r.data_format==='float64'?'selected':''}>float64</option><option value="int16" ${r.data_format==='int16'?'selected':''}>int16</option><option value="uint16" ${r.data_format==='uint16'?'selected':''}>uint16</option><option value="int32" ${r.data_format==='int32'?'selected':''}>int32</option><option value="uint32" ${r.data_format==='uint32'?'selected':''}>uint32</option><option value="bool" ${r.data_format==='bool'?'selected':''}>bool</option></select>`:escHtml(r.data_format||'')}</td>
                    <td>${canEdit?`<input type="text" class="reg-key" value="${escHtml(r.sensor_key||'')}" style="width:90px">`:`<code>${escHtml(r.sensor_key||'')}</code>`}</td>
                    <td>${canEdit?`<input type="text" class="reg-label" value="${escHtml(r.label||'')}" style="width:110px">`:escHtml(r.label||'')}</td>
                    <td>${canEdit?`<input type="text" class="reg-unit" value="${escHtml(r.unit||'')}" style="width:40px">`:escHtml(r.unit||'')}</td>
                    <td>${canEdit?`<input type="number" class="reg-scale" value="${r.scale!=null?r.scale:1}" step="any" style="width:60px">`:r.scale}</td>
                    ${canEdit?`<td><button class="btn btn-xs btn-red conn-reg-remove" data-idx="${i}" title="Sterge">x</button></td>`:''}
                </tr>`;
            });
            h += '</tbody></table>';
            tbl.innerHTML = h;
            if (canEdit) {
                tbl.querySelectorAll('.conn-reg-remove').forEach((btn: Element) => {
                    btn.addEventListener('click', () => {
                        const idx = parseInt((btn as HTMLElement).dataset.idx || '0', 10);
                        (window as any)._connRegs.splice(idx, 1);
                        _renderRegsTable((window as any)._connRegs);
                    });
                });
            }
        };

        // Load existing registers
        (window as any)._connRegs = [];
        if (mid) {
            fetch(`/api/config/registers/${encodeURIComponent(mid)}`, {credentials:'include'})
                .then(r=>r.json()).then((regs: any[]) => {
                    (window as any)._connRegs = regs.filter((r: any) => r.connection_target === 'plc');
                    _renderRegsTable((window as any)._connRegs);
                }).catch(()=>{});
        }

        // ── Add register row ──
        const addRegBtn = document.getElementById('conn-reg-add') as HTMLButtonElement | null;
        if (addRegBtn && canEdit) {
            addRegBtn.addEventListener('click', () => {
                const proto = (document.getElementById('detail-conn-protocol') as HTMLSelectElement | null)?.value || 'modbus_tcp';
                const defaults = {register_address:'',register_type: proto==='s7'?'db':proto==='opc_ua'?'node':proto==='mqtt'?'topic':'holding',
                    data_format:'float32',sensor_key:'',label:'',unit:'',scale:1.0,byte_order:'big_endian',offset_value:0,enabled:1};
                (window as any)._connRegs.push(defaults);
                _renderRegsTable((window as any)._connRegs);
            });
        }

        // ── Save all registers ──
        const saveRegsBtn = document.getElementById('conn-reg-save-all') as HTMLButtonElement | null;
        if (saveRegsBtn && mid && canEdit) {
            saveRegsBtn.addEventListener('click', async () => {
                const tbl = document.getElementById('conn-registers-table');
                const rows = tbl?.querySelectorAll('tbody tr') || [];
                const regs: any[] = [];
                const proto = (document.getElementById('detail-conn-protocol') as HTMLSelectElement | null)?.value || 'modbus_tcp';
                rows.forEach((tr: Element) => {
                    const addr = (tr.querySelector('.reg-addr') as HTMLInputElement | null)?.value?.trim();
                    if (!addr) return;
                    regs.push({
                        register_address: addr,
                        register_type: (tr.querySelector('.reg-type') as HTMLSelectElement | null)?.value || 'holding',
                        data_format: (tr.querySelector('.reg-fmt') as HTMLSelectElement | null)?.value || 'float32',
                        sensor_key: (tr.querySelector('.reg-key') as HTMLInputElement | null)?.value?.trim() || '',
                        label: (tr.querySelector('.reg-label') as HTMLInputElement | null)?.value?.trim() || '',
                        unit: (tr.querySelector('.reg-unit') as HTMLInputElement | null)?.value?.trim() || '',
                        scale: parseFloat((tr.querySelector('.reg-scale') as HTMLInputElement | null)?.value || '1') || 1.0,
                        protocol: proto,
                        byte_order: 'big_endian',
                        offset_value: 0,
                    });
                });
                saveRegsBtn.disabled = true; saveRegsBtn.textContent = 'Se salveaza...';
                try {
                    const res = await fetch(`/api/config/registers/${encodeURIComponent(mid)}`,
                        {method:'POST',headers:{'Content-Type':'application/json'},
                         body:JSON.stringify({registers:regs,connection_target:'plc'}),credentials:'include'});
                    const data = await res.json();
                    toast(`${data.count||0} registre salvate.`);
                    const fresh = await fetch(`/api/config/registers/${encodeURIComponent(mid)}`,{credentials:'include'}).then(r=>r.json());
                    (window as any)._connRegs = fresh.filter((r: any) => r.connection_target === 'plc');
                    _renderRegsTable((window as any)._connRegs);
                } catch(e: any) { toast('Eroare: '+e.message,'error'); }
                finally { saveRegsBtn.disabled=false; saveRegsBtn.textContent='Salveaza registre'; }
            });
        }

        // ── Load profiles dropdown ──
        const profileSelect = document.getElementById('conn-profile-select') as HTMLSelectElement | null;
        if (profileSelect) {
            fetch('/api/collector/profiles', {credentials:'include'})
                .then(r=>r.json()).then((profiles: any[]) => {
                    profiles.forEach((p: any) => {
                        const opt = document.createElement('option');
                        opt.value = p.device_type;
                        opt.textContent = `${p.device_type} (${p.protocol}) - ${(p.registers||[]).length} reg`;
                        profileSelect.appendChild(opt);
                    });
                }).catch(()=>{});
        }

        // ── Import profile ──
        const importBtn = document.getElementById('conn-profile-import') as HTMLButtonElement | null;
        if (importBtn && mid && canEdit) {
            importBtn.addEventListener('click', async () => {
                const dt = (document.getElementById('conn-profile-select') as HTMLSelectElement | null)?.value;
                if (!dt) { toast('Selecteaza un profil.','error'); return; }
                importBtn.disabled = true; importBtn.textContent = 'Se importa...';
                try {
                    const res = await fetch(`/api/collector/profiles/import/${encodeURIComponent(mid)}`,
                        {method:'POST',headers:{'Content-Type':'application/json'},
                         body:JSON.stringify({device_type:dt,connection_target:'plc'}),credentials:'include'});
                    const data = await res.json();
                    toast(`${data.imported||0} registre importate din ${dt}.`);
                    const fresh = await fetch(`/api/config/registers/${encodeURIComponent(mid)}`,{credentials:'include'}).then(r=>r.json());
                    (window as any)._connRegs = fresh.filter((r: any) => r.connection_target === 'plc');
                    _renderRegsTable((window as any)._connRegs);
                } catch(e: any) { toast('Eroare: '+e.message,'error'); }
                finally { importBtn.disabled=false; importBtn.textContent='Importa registre din profil'; }
            });
        }

        // ── Load connection log ──
        const logList = document.getElementById('conn-log-list');
        if (logList && mid) {
            fetch(`/api/connection/log/${encodeURIComponent(mid)}`, {credentials:'include'})
                .then(r=>r.json()).then((logs: any[]) => {
                    if (!logs || logs.length === 0) {
                        logList.innerHTML = '<span style="color:var(--clr-muted)">Nicio inregistrare</span>';
                        return;
                    }
                    let h = '';
                    logs.forEach((l: any) => {
                        const color = l.status==='ok' ? '#2a2' : '#c22';
                        const ts = l.timestamp ? l.timestamp.replace('T',' ').substring(0,19) : '';
                        h += `<div style="padding:3px 0;border-bottom:1px solid var(--clr-border,#333)">
                            <span style="color:${color};font-weight:600">${l.status==='ok'?'&#10004;':'&#10008;'}</span>
                            <span style="color:var(--clr-muted)">${ts}</span>
                            <span>${escHtml(l.protocol||'')} - ${escHtml(l.message||'')}</span>
                            ${l.values_read ? `<span style="color:var(--clr-muted)">(${l.values_read} val)</span>` : ''}
                        </div>`;
                    });
                    logList.innerHTML = h;
                }).catch(()=>{});
        }

    } else if (detailTabActive === 'logs') {
        if (d.logs.length === 0) {
            el.innerHTML = '<div class="alarm-empty"><span class="icon">📋</span>Nicio înregistrare în jurnal</div>';
            return;
        }
        const allOperators = [...new Set(d.logs.map((l: any) => l.technician || '').filter(Boolean))].sort() as string[];
        const selOp = (window as any)._logFilterOperator || '';
        const filteredLogs = selOp ? d.logs.filter((l: any) => (l.technician || '') === selOp) : d.logs;

        let html = '<div class="detail-filter-bar"><label>Operator: <select id="log-filter-op"><option value="">Toți</option>';
        allOperators.forEach((op: string) => { html += `<option value="${escHtml(op)}"${op === selOp ? ' selected' : ''}>${escHtml(op)}</option>`; });
        html += '</select></label></div>';

        html += '<table><thead><tr><th>Data</th><th>Lucrare</th><th>Operator</th><th>Ore</th><th>Note</th></tr></thead><tbody>';
        filteredLogs.forEach((l: any) => {
            html += `<tr><td>${escHtml(formatDateRO(l.date))}</td><td>${escHtml(truncate(l.action, 60))}</td><td>${escHtml(l.technician)}</td><td>${l.hours_at_service || '—'}</td><td>${escHtml(truncate(l.notes, 50))}</td></tr>`;
        });
        html += '</tbody></table>';
        el.innerHTML = html;

        document.getElementById('log-filter-op')?.addEventListener('change', (e) => {
            (window as any)._logFilterOperator = (e.target as HTMLSelectElement).value;
            renderDetailTab();
        });
    } else if (detailTabActive === 'sensors') {
        _renderSensorsTab(el, d);
    } else if (detailTabActive === 'molds') {
        await _renderMoldsTab(el, d);
    } else if (detailTabActive === 'etichete') {
        await renderEticheteTab(el, (window as any)._detailLineId);
    } else if (detailTabActive === 'imprimanta') {
        await renderImprimantaTab(el, (window as any)._detailLineId);
    } else if (detailTabActive === 'hmi_mirror') {
        const hmiUrl = (window as any)._detailHmiUrl || '';
        if (!hmiUrl) {
            el.innerHTML = '<div class="alarm-empty"><span class="icon">🖥</span>Această mașină nu are un HMI web configurat. Configurați URL-ul HMI din setările mașinii.</div>';
        } else {
            el.innerHTML = `<div class="hmi-mirror-container">
                <div class="hmi-mirror-toolbar">
                    <span class="hmi-mirror-label">🖥 HMI Live — ${escHtml(hmiUrl)}</span>
                    <button class="btn btn-sm hmi-fullscreen-btn" id="hmi-fullscreen-btn">⛶ Ecran complet</button>
                    <a href="${escHtml(hmiUrl)}" target="_blank" class="btn btn-sm">↗ Tab nou</a>
                </div>
                <iframe src="${escHtml(hmiUrl)}" class="hmi-mirror-iframe" id="hmi-mirror-iframe" allowfullscreen></iframe>
            </div>`;
            document.getElementById('hmi-fullscreen-btn')?.addEventListener('click', () => {
                const iframe = document.getElementById('hmi-mirror-iframe') as any;
                if (iframe?.requestFullscreen) iframe.requestFullscreen();
                else if (iframe?.webkitRequestFullscreen) iframe.webkitRequestFullscreen();
            });
        }
    }
}

/* ═══ MATRIȚE TAB — listă matrițe linkate + activă + istoric + schimbare ═══ */

async function _renderMoldsTab(el: any, d: any): Promise<void> {
    const m = d.machine || {};
    const machineId = m.id || (window as any)._detailLineId;
    if (!machineId) {
        el.innerHTML = '<div class="alarm-empty">Fără mașină selectată</div>';
        return;
    }

    // Caută toate matrițele linkate la această mașină (linked_machine_id == machineId)
    let allMolds: any[] = [];
    try {
        const r = await fetch('/api/machines');
        if (r.ok) {
            const all = await r.json();
            allMolds = (all || []).filter((mm: any) =>
                mm.machine_type === 'mold' &&
                ((mm.linked_machine_id || '').trim() === machineId || (mm.line || '').trim() === (m.line || '').trim())
            );
        }
    } catch (e) { console.error('Failed to load machines:', e); }

    // Caută istoric schimbări matriță
    let history: any[] = [];
    try {
        const r = await fetch(`/api/machines/${encodeURIComponent(machineId)}/mold_history?limit=20`);
        if (r.ok) history = await r.json();
    } catch (e) { console.error('Failed to load mold history:', e); }

    const activeId = (m.active_mold_id || '').trim();
    const isSACMI = m.machine_type === 'compression';

    let html = `<div class="molds-tab-container">
        <div class="molds-summary">
            <div class="molds-summary-item">
                <span class="molds-summary-label">Mașină</span>
                <span class="molds-summary-val">${escHtml(machineId)}</span>
            </div>
            <div class="molds-summary-item">
                <span class="molds-summary-label">Tip</span>
                <span class="molds-summary-val">${isSACMI ? 'Compresie SACMI' : 'Injecție IMM'}</span>
            </div>
            <div class="molds-summary-item">
                <span class="molds-summary-label">Matriță activă</span>
                <span class="molds-summary-val molds-active-name">${activeId ? escHtml(activeId) : '— niciuna —'}</span>
            </div>
        </div>

        <div class="molds-actions-bar">
            <button class="btn btn-primary" id="btn-change-mold">🔄 Schimbă matrița activă</button>
            <button class="btn btn-secondary" id="btn-refresh-molds">↻ Reîncarcă</button>
        </div>

        <h4 class="molds-section-title">Matrițe disponibile pentru această mașină</h4>
        <table class="molds-list-table">
            <thead>
                <tr>
                    <th>Status</th>
                    <th>Matriță</th>
                    <th>Cuiburi</th>
                    <th>Cicluri</th>
                    <th>Ore</th>
                    ${isSACMI ? '<th>Timp ciclu nominal</th>' : ''}
                    <th>Cod</th>
                </tr>
            </thead>
            <tbody>`;

    if (allMolds.length === 0) {
        html += `<tr><td colspan="${isSACMI ? 7 : 6}" class="molds-empty">Nu există matrițe asociate. Configurează din Configurare Fabrică.</td></tr>`;
    } else {
        for (const mold of allMolds) {
            const isActive = mold.id === activeId;
            const cycles = parseInt(mold.cycle_count || '0').toLocaleString('ro-RO');
            const hours = parseFloat(mold.operating_hours || '0').toFixed(1);
            const cavities = mold.cavities || '—';
            const ct = mold.nominal_cycle_time_sec || '—';
            html += `<tr class="${isActive ? 'mold-row-active' : 'mold-row-inactive'}">
                <td>${isActive ? '<span class="mold-badge-active">ACTIVĂ</span>' : '<span class="mold-badge-inactive">inactivă</span>'}</td>
                <td><strong>${escHtml(mold.name || mold.id)}</strong></td>
                <td>${cavities}</td>
                <td>${cycles}</td>
                <td>${hours} h</td>
                ${isSACMI ? `<td>${ct} s</td>` : ''}
                <td><code>${escHtml(mold.id)}</code></td>
            </tr>`;
        }
    }
    html += `</tbody></table>

        <h4 class="molds-section-title">Istoric schimbări (ultimele ${history.length})</h4>
        <table class="molds-history-table">
            <thead>
                <tr>
                    <th>Data</th>
                    <th>Sursă</th>
                    <th>De la</th>
                    <th>La</th>
                    <th>Cicluri matriță veche</th>
                    <th>Operator</th>
                    <th>Note</th>
                </tr>
            </thead>
            <tbody>`;
    if (history.length === 0) {
        html += `<tr><td colspan="7" class="molds-empty">Nu există schimbări înregistrate.</td></tr>`;
    } else {
        for (const h of history) {
            const sourceBadge = h.detection_source === 'auto_recipe'
                ? '<span class="badge badge-yellow" title="Detecție automată din EM63">⚠ AUTO</span>'
                : h.detection_source === 'manual'
                ? '<span class="badge badge-green">✓ Manual</span>'
                : `<span class="badge badge-gray">${escHtml(h.detection_source)}</span>`;
            html += `<tr>
                <td>${escHtml(h.changed_at || '')}</td>
                <td>${sourceBadge}</td>
                <td>${escHtml(h.old_mold_id || '—')}</td>
                <td><strong>${escHtml(h.new_mold_id || '')}</strong></td>
                <td>${h.cycle_count_at_change != null ? Number(h.cycle_count_at_change).toLocaleString('ro-RO') : '—'}</td>
                <td>${escHtml(h.changed_by || '—')}</td>
                <td class="molds-notes">${escHtml(h.notes || '')}</td>
            </tr>`;
        }
    }
    html += `</tbody></table></div>`;

    el.innerHTML = html;

    // Event handlers
    document.getElementById('btn-refresh-molds')?.addEventListener('click', () => renderDetailTab());

    document.getElementById('btn-change-mold')?.addEventListener('click', () => {
        _showChangeMoldModal(machineId, activeId, allMolds);
    });
}

function _showChangeMoldModal(machineId: string, currentMoldId: string, availableMolds: any[]): void {
    const candidates = availableMolds.filter(m => m.id !== currentMoldId);
    if (candidates.length === 0) {
        alert('Nu există alte matrițe disponibile pentru această mașină. Configurează matrițe noi din Configurare Fabrică.');
        return;
    }

    let optionsHtml = '<option value="">— Selectează matriță —</option>';
    candidates.forEach(m => {
        const cav = m.cavities ? ` (${m.cavities} cuiburi)` : '';
        optionsHtml += `<option value="${escHtml(m.id)}">${escHtml(m.name || m.id)}${cav}</option>`;
    });

    const modalHtml = `<div class="modal-overlay" id="change-mold-modal-overlay">
        <div class="modal" style="max-width:520px">
            <h3>🔄 Schimbă matrița activă pe ${escHtml(machineId)}</h3>
            <form id="change-mold-form">
                <div class="form-row">
                    <label>Matrița curentă (activă):</label>
                    <input type="text" value="${escHtml(currentMoldId || '— niciuna —')}" disabled>
                </div>
                <div class="form-row">
                    <label>Matrița nouă activă:</label>
                    <select id="change-mold-select" required>${optionsHtml}</select>
                </div>
                <div class="form-row">
                    <label>Note (opțional):</label>
                    <textarea id="change-mold-notes" rows="3" placeholder="Motiv schimbare, lucrare efectuată, observații..."></textarea>
                </div>
                <div class="modal-actions">
                    <button type="button" class="btn btn-secondary" id="change-mold-cancel">Anulează</button>
                    <button type="submit" class="btn btn-primary">✓ Confirmă schimbarea</button>
                </div>
            </form>
        </div>
    </div>`;

    const div = document.createElement('div');
    div.innerHTML = modalHtml;
    document.body.appendChild(div.firstElementChild!);

    const close = () => {
        const overlay = document.getElementById('change-mold-modal-overlay');
        if (overlay) overlay.remove();
    };

    document.getElementById('change-mold-cancel')?.addEventListener('click', close);
    document.getElementById('change-mold-modal-overlay')?.addEventListener('click', (e) => {
        if ((e.target as HTMLElement).id === 'change-mold-modal-overlay') close();
    });

    document.getElementById('change-mold-form')?.addEventListener('submit', async (e) => {
        e.preventDefault();
        const sel = document.getElementById('change-mold-select') as HTMLSelectElement;
        const notes = (document.getElementById('change-mold-notes') as HTMLTextAreaElement)?.value || '';
        const newMoldId = sel.value;
        if (!newMoldId) { alert('Selectează o matriță'); return; }
        try {
            const r = await fetch('/api/molds/change', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    machine_id: machineId,
                    new_mold_id: newMoldId,
                    notes: notes,
                    detection_source: 'manual'
                })
            });
            const data = await r.json();
            if (!r.ok) { alert('Eroare: ' + (data.error || data.detail || 'necunoscut')); return; }
            close();
            alert(`Matriță schimbată: ${data.old_mold_id || '—'} → ${data.new_mold_id}`);
            // Reîncarcă datele linie + tab
            try {
                const fresh = await api(`/api/line_detail/${encodeURIComponent((window as any)._detailLineId)}`);
                (window as any)._detailData = fresh;
                renderDetailTab();
            } catch (e) { console.error(e); }
        } catch (err) {
            alert('Eroare rețea: ' + err);
        }
    });
}

/* ═══ SENZORI TAB — afișare clară, card-uri mari, clickabile ════════ */

// AUDIT 2026-05-13 — chei electrice PAC3220 (Modbus) duplicate între tab-ul Senzori
// și tab-ul Consum. Le excludem din randarea Senzori; rămân vizibile în Consum
// (vezi energy-stats.ts → _renderEnergyMonitor). DB neatinsă: filtrul e doar UI.
// Nu se exclud: vars EM63 (cycle_count, oil_temp, clp_*, heat_energy_cycle),
// status text (machine_status, current_mold, machine_state) sau senzori manuali
// adăugați de operator/master.
const PAC3220_ELECTRIC_KEYS = new Set<string>([
    'power', 'power_l1', 'power_l2', 'power_l3',
    'apparent_power', 'reactive_power',
    'current_l1', 'current_l2', 'current_l3', 'current_n',
    'voltage_l1l2', 'voltage_l1n', 'voltage_l2l3',
    'voltage_l2n', 'voltage_l3l1', 'voltage_l3n',
    'pf_l1', 'pf_l2', 'pf_l3', 'pf_total',
    'frequency',
    'consumption',
    'consum',
]);

const SENSOR_META: Record<string, { label: string; icon: string; desc: string }> = {
    power: { label: 'Putere', icon: '⚡', desc: 'Putere activă consumată de echipament' },
    apparent_power: { label: 'Putere aparentă', icon: '⚡', desc: 'Putere aparentă (include pierderi reactive)' },
    reactive_power: { label: 'Putere reactivă', icon: '⚡', desc: 'Putere reactivă — componenta pierdută în câmp electromagnetic' },
    consum: { label: 'Consum', icon: '⚡', desc: 'Consum instantaneu de energie' },
    total_power: { label: 'Putere totală', icon: '⚡', desc: 'Putere totală activă pe toate fazele' },
    power_l1: { label: 'Putere L1', icon: '⚡', desc: 'Putere activă pe faza L1' },
    power_l2: { label: 'Putere L2', icon: '⚡', desc: 'Putere activă pe faza L2' },
    power_l3: { label: 'Putere L3', icon: '⚡', desc: 'Putere activă pe faza L3' },
    current_l1: { label: 'Curent L1', icon: '🔌', desc: 'Curentul electric pe faza L1' },
    current_l2: { label: 'Curent L2', icon: '🔌', desc: 'Curentul electric pe faza L2' },
    current_l3: { label: 'Curent L3', icon: '🔌', desc: 'Curentul electric pe faza L3' },
    current_n: { label: 'Curent N', icon: '🔌', desc: 'Curentul pe conductorul de neutru' },
    voltage_l1n: { label: 'Tensiune L1-N', icon: '🔋', desc: 'Tensiunea între faza L1 și neutru' },
    voltage_l2n: { label: 'Tensiune L2-N', icon: '🔋', desc: 'Tensiunea între faza L2 și neutru' },
    voltage_l3n: { label: 'Tensiune L3-N', icon: '🔋', desc: 'Tensiunea între faza L3 și neutru' },
    voltage_l1l2: { label: 'Tensiune L1-L2', icon: '🔋', desc: 'Tensiunea între fazele L1 și L2' },
    voltage_l2l3: { label: 'Tensiune L2-L3', icon: '🔋', desc: 'Tensiunea între fazele L2 și L3' },
    voltage_l3l1: { label: 'Tensiune L3-L1', icon: '🔋', desc: 'Tensiunea între fazele L3 și L1' },
    frequency: { label: 'Frecvență', icon: '〜', desc: 'Frecvența rețelei electrice (50Hz nominal)' },
    factor: { label: 'Factor putere', icon: '📊', desc: 'Factor de putere — eficiența utilizării energiei (ideal: >0.95)' },
    factor_l1: { label: 'Factor P. L1', icon: '📊', desc: 'Factor de putere pe faza L1' },
    factor_l2: { label: 'Factor P. L2', icon: '📊', desc: 'Factor de putere pe faza L2' },
    factor_l3: { label: 'Factor P. L3', icon: '📊', desc: 'Factor de putere pe faza L3' },
    counter: { label: 'Contor energie', icon: '🔢', desc: 'Contorul total de energie consumată (kWh cumulat)' },
    counter_production: { label: 'Contor producție', icon: '🔢', desc: 'Piese produse — contor din PLC' },
    temperature: { label: 'Temperatură', icon: '🌡', desc: 'Temperatura procesului sau echipamentului' },
    temp_1: { label: 'Temperatură 1', icon: '🌡', desc: 'Senzor temperatură canal 1' },
    temp_2: { label: 'Temperatură 2', icon: '🌡', desc: 'Senzor temperatură canal 2' },
    pressure: { label: 'Presiune', icon: '🔵', desc: 'Presiunea în circuit (bar)' },
    speed: { label: 'Viteză', icon: '⏱', desc: 'Viteza motorului sau procesului' },
    vibration_x: { label: 'Vibrație X', icon: '📳', desc: 'Nivel vibrații pe axa X' },
    vibration_y: { label: 'Vibrație Y', icon: '📳', desc: 'Nivel vibrații pe axa Y' },
    capacity: { label: 'Capacitate', icon: '📦', desc: 'Capacitate curentă de producție' },
    duration: { label: 'Durată ciclu', icon: '⏱', desc: 'Durata unui ciclu de producție' },
    machine_state: { label: 'Stare mașină', icon: '🏭', desc: 'Starea curentă a mașinii din PLC (producție, oprită, mentenanță)' },
    cycles: { label: 'Cicluri', icon: '🔄', desc: 'Numărul total de cicluri efectuate' },
    hours: { label: 'Ore funcționare', icon: '⏱', desc: 'Orele totale de funcționare ale echipamentului' },
    resistivity: { label: 'Rezistivitate', icon: '💧', desc: 'Rezistivitatea apei de răcire' },
    cycle_count: { label: 'Contor cicluri', icon: '🔢', desc: 'Contor total cicluri mașină (EUROMAP 63 — @ActCntCycMachTotal)' },
    oil_temp: { label: 'Temp. ulei', icon: '🌡', desc: 'Temperatura uleiului hidraulic (EM63 — @ActTmpOilAE)' },
    clp_close_time: { label: 'Timp închidere matriță', icon: '⏱', desc: 'Durata închiderii matriței în ultimul ciclu (EM63 — @ActTimClpClsCyc)' },
    clp_open_time: { label: 'Timp deschidere matriță', icon: '⏱', desc: 'Durata deschiderii matriței în ultimul ciclu (EM63 — @ActTimClpOpnCyc)' },
    heat_energy_cycle: { label: 'Energie încălzire/ciclu', icon: '🔥', desc: 'Energia consumată de rezistenții cilindrului per ciclu (EM63 — @ActEnrgProcesMtrEnergyHeatCyc)' },
    machine_status: { label: 'Status mașină', icon: '🏭', desc: 'Stare actuală mașină din PLC (EM63 — @ActStsMach: AAA01=auto, MAN=manual, etc.)' },
    current_mold: { label: 'Matriță activă', icon: '🧩', desc: 'Descrierea matriței active introdusă în HMI (EM63 — @ActDescMld)' },
    cycle_time: { label: 'Timp ciclu', icon: '⏱', desc: 'Durata totală a ciclului de injecție în secunde (EM63 — @ActTimCyc)' },
    injection_time: { label: 'Timp injecție', icon: '💉', desc: 'Timp de umplere matriță prin injecție în secunde (EM63 — @ActTimFill[1])' },
    cushion_volume: { label: 'Volum pernă', icon: '🛏', desc: 'Volumul rămas în cilindru la sfârșitul fazei de injecție (cm³, EM63 — @ActVolCsh[1])' },
    injection_pressure_max: { label: 'Presiune max injecție', icon: '🔵', desc: 'Presiunea maximă atinsă în timpul injecției (bar, EM63 — @ActPrsMachSpecMax)' },
    xfr_pressure: { label: 'Presiune comutare', icon: '🔵', desc: 'Presiunea de comutare HV→HD (bar, EM63 — @ActPrsXfrSpec[1])' },
    mold_open_stroke: { label: 'Cursa deschidere matriță', icon: '↔', desc: 'Cursa de deschidere a matriței (mm, EM63 — @ActStrClpOpnCyc)' },
    ejector_fwd_stroke: { label: 'Cursa avans extractor', icon: '↗', desc: 'Cursa de avans a extractorului (mm, EM63 — @ActStrEjeFwdCyc)' },
    ejector_retr_stroke: { label: 'Cursa retragere extractor', icon: '↙', desc: 'Cursa de retragere a extractorului (mm, EM63 — @ActStrEjeRetrCyc)' },
    energy_total_cycle: { label: 'Energie totală/ciclu', icon: '⚡', desc: 'Energia totală consumată per ciclu (W, EM63 — @ActEnrgProcesMtrEnergyTotalCyc)' },
    machine_status_ext: { label: 'Status mașină extins', icon: '🏭', desc: 'Status extins (alpha 5) din EM63 — @ActStsMachExt' },
};

function _getSensorMeta(sensorKey: string): { label: string; icon: string; desc: string } {
    return SENSOR_META[sensorKey] || { label: sensorKey, icon: '📡', desc: `Senzor: ${sensorKey}` };
}

function _buildSensorHistoryChart(hData: { t: number; v: number }[], color: string, unit: string): string {
    if (!hData.length) return '<div class="stab-no-history">Fără date istorice momentan</div>';
    const w = 400, h = 80, pad = 5;
    const vals = hData.map(d => d.v);
    const mn = Math.min(...vals), mx = Math.max(...vals);
    const range = mx - mn || 1;
    const points = hData.map((d, i) => {
        const x = pad + (i / (hData.length - 1 || 1)) * (w - 2 * pad);
        const y = h - pad - ((d.v - mn) / range) * (h - 2 * pad);
        return `${x},${y}`;
    }).join(' ');
    return `<svg viewBox="0 0 ${w} ${h}" class="stab-chart-svg">
        <polyline points="${points}" fill="none" stroke="${color}" stroke-width="2" stroke-linejoin="round"/>
        <text x="${pad}" y="${h - 2}" fill="var(--text-muted)" font-size="9">${mn.toFixed(1)} ${escHtml(unit)}</text>
        <text x="${w - pad}" y="10" fill="var(--text-muted)" font-size="9" text-anchor="end">${mx.toFixed(1)} ${escHtml(unit)}</text>
    </svg>`;
}

function _renderSensorsTab(el: HTMLElement, d: any): void {
    const lineId = (window as any)._detailLineId as string;
    const cfgs = (state.sensorConfigs || {})[lineId] || [];
    if (!cfgs.length) {
        el.innerHTML = '<div class="alarm-empty"><span class="icon">📡</span>Niciun senzor configurat pentru acest echipament.</div>';
        return;
    }

    // AUDIT 2026-05-13 — exclude cheile PAC3220 din randarea Senzori (sunt vizibile în tab Consum).
    const visibleCfgs = cfgs.filter((c: any) => !PAC3220_ELECTRIC_KEYS.has(c.sensor_key));
    if (!visibleCfgs.length) {
        el.innerHTML = '<div class="alarm-empty"><span class="icon">⚡</span>Senzorii electrici (curent, tensiune, putere, energie) sunt în tab-ul Consum. Senzori de proces nu sunt încă configurați pentru acest echipament.</div>';
        return;
    }

    const hist = state.sensorHistory[lineId] || {};
    const isMold = d.machine?.machine_type === 'mold';
    const gColor = (val: number, warn: number, crit: number): string => {
        const sv = getSensorSeverity(val, warn, crit);
        return sv === 'critical' ? '#ff3d3d' : sv === 'warning' ? '#ffaa00' : '#00e676';
    };

    let html = '<div class="sensors-tab-container">';
    html += `<div class="sensors-tab-header"><span class="sensors-tab-count">${visibleCfgs.filter((c: any) => c.enabled).length} senzori activi</span></div>`;
    html += '<div class="sensors-tab-list">';

    visibleCfgs.forEach((cfg: any) => {
        if (!cfg.enabled) return;
        const meta = _getSensorMeta(cfg.sensor_key);
        // AUDIT 2026-05-14 — split between "any value present" (used to show stale
        // value with ÎNTÂRZIAT badge) and "fresh" (used for severity / sparkline).
        const rawServerVal: any = isMold
            ? (cfg.sensor_key === 'cycles' ? ((state.moldCounters || {})[lineId]?.cycles) : cfg.sensor_key === 'hours' ? ((state.moldCounters || {})[lineId]?.hours) : undefined)
            : getSensorRaw(lineId, cfg.sensor_key);
        const hasAnyValue = rawServerVal !== undefined && rawServerVal !== null;
        // Freshness for non-mold sensors. Mold counters live in DB → treated as LIVE.
        const updAt = !isMold && hasAnyValue ? getSensorUpdatedAt(lineId, cfg.sensor_key) : null;
        const fresh: 'live' | 'stale' | 'offline' = isMold
            ? (hasAnyValue ? 'live' : 'offline')
            : (hasAnyValue
                ? (updAt != null ? getSensorFreshness(updAt) : 'live')  // legacy shape: no timestamp → assume live
                : 'offline');
        // hasReal = display value with normal styling (LIVE for non-mold, mold counters always).
        // hasStaleValue = show value but with ÎNTÂRZIAT badge + dimmed color.
        const hasReal = isMold ? hasAnyValue : (fresh === 'live');
        const hasStaleValue = !isMold && hasAnyValue && fresh === 'stale';
        const isRealtime = hasReal && !isMold;
        const displayVal: any = (hasReal || hasStaleValue) ? rawServerVal : null;
        const numVal = (hasReal || hasStaleValue) ? parseFloat(String(displayVal)) : NaN;
        const isText = (hasReal || hasStaleValue) && !isMold && typeof displayVal === 'string' && isNaN(numVal);
        const valueAvailable = hasReal || hasStaleValue;
        // Color: stale values are tinted with the warning palette so the operator sees
        // immediately that the reading is not fresh. Fresh numeric values use severity.
        let color: string;
        if (hasReal && !isNaN(numVal)) color = gColor(numVal, cfg.warn_threshold, cfg.crit_threshold);
        else if (hasStaleValue) color = '#ffaa00';
        else if (isText) color = '#9ca3af';
        else color = '#5b6473';
        const severity = hasReal && !isNaN(numVal) ? getSensorSeverity(numVal, cfg.warn_threshold, cfg.crit_threshold) : 'normal';
        const hData = (hist[cfg.sensor_key] || []).map((v: number, i: number) => ({ t: i, v }));
        const pct = hasReal && cfg.crit_threshold > 0 && !isNaN(numVal) ? Math.min(100, Math.round(numVal / (cfg.crit_threshold * 1.1) * 100)) : 0;

        let fmtVal: string;
        if (!valueAvailable) {
            fmtVal = '—';
        } else if (isText) {
            fmtVal = String(displayVal);
        } else if (cfg.sensor_key === 'cycles' && !isNaN(numVal)) {
            fmtVal = Number(numVal).toLocaleString('ro-RO');
        } else if (cfg.sensor_key === 'cycle_count' && !isNaN(numVal)) {
            fmtVal = Number(numVal).toLocaleString('ro-RO');
        } else if (cfg.sensor_key === 'hours' && !isNaN(numVal)) {
            fmtVal = Number(numVal).toFixed(1);
        } else {
            fmtVal = String(displayVal);
        }

        let threshInfo = '';
        if (cfg.warn_threshold > 0 || cfg.crit_threshold > 0) {
            threshInfo = '<div class="stab-thresholds">';
            if (cfg.warn_threshold > 0) threshInfo += `<span class="stab-thresh warn">Avertizare: ${cfg.warn_threshold} ${escHtml(cfg.unit)}</span>`;
            if (cfg.crit_threshold > 0) threshInfo += `<span class="stab-thresh crit">Critic: ${cfg.crit_threshold} ${escHtml(cfg.unit)}</span>`;
            threshInfo += '</div>';
        }

        // AUDIT 2026-05-14 — badge 3-state: LIVE / ÎNTÂRZIAT / neconectat
        //  - LIVE (< 5 min)             — colector trimite proaspăt, valoare fiabilă
        //  - ÎNTÂRZIAT (5 min – 30 min) — date vechi, valoarea încă afișată dar dimmed
        //  - neconectat (> 30 min sau lipsă timestamp) — comportament vechi
        let liveBadge: string;
        if (isMold) {
            liveBadge = hasAnyValue
                ? '<span class="stab-source-badge live" title="Valoare reală din DB (machines.cycle_count / operating_hours)">LIVE</span>'
                : '<span class="stab-source-badge unconnected" title="Contor matriță neactualizat — necesită colector EM63 / OPC-UA pentru cycle_count">neconectat</span>';
        } else if (fresh === 'live' && hasAnyValue) {
            liveBadge = '<span class="stab-source-badge live" title="Valoare reală din telemetrie (EM63 / PAC3220 Modbus / SFM12LD)">LIVE</span>';
        } else if (fresh === 'stale' && updAt != null) {
            const lastTs = Date.parse(/Z$|[+-]\d{2}:?\d{2}$/.test(updAt) ? updAt : updAt + 'Z');
            const ageMin = Number.isFinite(lastTs) ? Math.max(1, Math.round((Date.now() - lastTs) / 60000)) : null;
            const hhmm = Number.isFinite(lastTs) ? new Date(lastTs).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' }) : '?';
            const tip = ageMin != null
                ? `Ultima citire: ${hhmm} (${ageMin} min în urmă). Colectorul nu trimite date noi.`
                : 'Date întârziate (>5 min). Colectorul nu trimite citiri recente.';
            liveBadge = `<span class="stab-source-badge stale" title="${escHtml(tip)}">ÎNTÂRZIAT</span>`;
        } else {
            liveBadge = '<span class="stab-source-badge unconnected" title="Senzor configurat dar fără sursă de date conectată. Date vor apărea când colectorul respectiv va fi configurat.">neconectat</span>';
        }

        const sparklineHtml = (!hasReal || isText) ? '' : `<div class="stab-sparkline">${buildSparklineSVG(hData, color)}</div>`;
        const cardClass = !valueAvailable ? 'unconnected' : (isRealtime || isMold ? 'realtime' : 'simulated');

        html += `
        <div class="stab-card ${severity}${isText ? ' text-value' : ''} ${cardClass}" data-sensor-key="${cfg.sensor_key}">
            <div class="stab-card-main" onclick="this.parentElement.classList.toggle('expanded')">
                <div class="stab-card-left">
                    <div class="stab-gauge-mini">${buildGaugeSVG(pct, '', color)}</div>
                </div>
                <div class="stab-card-center">
                    <div class="stab-label">${escHtml(cfg.label || meta.label)} ${liveBadge}</div>
                    <div class="stab-sensor-key">${escHtml(cfg.sensor_key)}</div>
                </div>
                <div class="stab-card-right">
                    <div class="stab-value" style="color:${color}" data-stab-val="${cfg.sensor_key}">${escHtml(fmtVal)}</div>
                    <div class="stab-unit">${valueAvailable ? escHtml(cfg.unit) : ''}</div>
                </div>
                ${sparklineHtml}
                <div class="stab-expand-icon">▼</div>
            </div>
            <div class="stab-card-details">
                <div class="stab-description"><span class="stab-desc-icon">${meta.icon}</span> ${escHtml(meta.desc)}</div>
                ${!valueAvailable ? '<div class="stab-unconnected-note">⚠ Acest senzor nu este conectat la un colector. Datele vor apărea automat când va fi conectat.</div>' : ''}
                ${hasStaleValue ? '<div class="stab-unconnected-note" style="border-left-color:#ffaa00;background:rgba(255,170,0,.08)">⏱ Citirea afișată e mai veche de 5 minute. Verifică colectorul.</div>' : ''}
                ${threshInfo}
                ${(!hasReal || isText) ? '' : `<div class="stab-history-label">Istoric recent:</div>
                <div class="stab-history-chart" data-stab-chart="${cfg.sensor_key}">
                    ${_buildSensorHistoryChart(hData, color, cfg.unit)}
                </div>`}
            </div>
        </div>`;
    });

    html += '</div></div>';
    el.innerHTML = html;
}

export function _updateSensorsTabLive(lineId: string, cfgs: any[], isMold: boolean): void {
    // AUDIT 2026-05-13 — consistent cu _renderSensorsTab: nu mai actualizăm celule
    // pentru cheile PAC3220 (DOM-ul nu le mai conține după filtrul de randare).
    const visibleCfgs = cfgs.filter((c: any) => !PAC3220_ELECTRIC_KEYS.has(c.sensor_key));
    visibleCfgs.forEach((cfg: any) => {
        if (!cfg.enabled) return;
        const valEl = document.querySelector(`[data-stab-val="${cfg.sensor_key}"]`) as HTMLElement | null;
        if (!valEl) return;
        // AUDIT 2026-05-14 — mirror _renderSensorsTab logic: show stale values dimmed.
        const rawServerVal: any = isMold
            ? (cfg.sensor_key === 'cycles' ? ((state.moldCounters || {})[lineId]?.cycles) : cfg.sensor_key === 'hours' ? ((state.moldCounters || {})[lineId]?.hours) : undefined)
            : getSensorRaw(lineId, cfg.sensor_key);
        const hasAnyValue = rawServerVal !== undefined && rawServerVal !== null;
        const updAt = !isMold && hasAnyValue ? getSensorUpdatedAt(lineId, cfg.sensor_key) : null;
        const fresh: 'live' | 'stale' | 'offline' = isMold
            ? (hasAnyValue ? 'live' : 'offline')
            : (hasAnyValue ? (updAt != null ? getSensorFreshness(updAt) : 'live') : 'offline');
        const hasReal = isMold ? hasAnyValue : (fresh === 'live');
        const hasStaleValue = !isMold && hasAnyValue && fresh === 'stale';
        const valueAvailable = hasReal || hasStaleValue;
        const numVal = valueAvailable ? parseFloat(String(rawServerVal)) : NaN;
        const isText = valueAvailable && !isMold && typeof rawServerVal === 'string' && isNaN(numVal);
        const severity = hasReal && !isNaN(numVal) ? getSensorSeverity(numVal, cfg.warn_threshold, cfg.crit_threshold) : 'normal';
        let color: string;
        if (hasReal) {
            color = (severity === 'critical' ? '#ff3d3d' : severity === 'warning' ? '#ffaa00' : (isText ? '#9ca3af' : '#00e676'));
        } else if (hasStaleValue) {
            color = '#ffaa00';
        } else {
            color = '#5b6473';
        }
        let fmtVal: string;
        if (!valueAvailable) {
            fmtVal = '—';
        } else if (cfg.sensor_key === 'cycles' && !isNaN(numVal)) {
            fmtVal = Number(numVal).toLocaleString('ro-RO');
        } else if (cfg.sensor_key === 'cycle_count' && !isNaN(numVal)) {
            fmtVal = Number(numVal).toLocaleString('ro-RO');
        } else if (cfg.sensor_key === 'hours' && !isNaN(numVal)) {
            fmtVal = Number(numVal).toFixed(1);
        } else {
            fmtVal = String(rawServerVal);
        }
        valEl.textContent = fmtVal;
        valEl.style.color = color;
        const card = valEl.closest('.stab-card');
        if (card) {
            card.classList.remove('normal', 'warning', 'critical');
            card.classList.add(severity);
        }
    });
}
